// The interface strings — the SOURCE `UiMessages` derives from, so `it/ui.ts` cannot drift.
// No `as const`: values widen to `string`, or a translation would have to repeat the English.
export const EN_UI = {
  app: {
    name: 'Volée',
    // The boot screen's name, written into index.html at build time: in Italian, the one language there.
    bootLabel: 'Volée Football, loading',
    navigationLabel: 'Main navigation',
    openMenu: 'Open the navigation menu',
    closeMenu: 'Close the navigation menu',
    collapseMenu: 'Narrow the menu to its icons',
    expandMenu: 'Show the menu labels',
  },
  // The entrance, the screen before the sign-in (apps/frontend/docs/ingresso-a-tutto-schermo.md).
  entry: {
    headline: { lead: 'A match is a moment.', accent: 'Growing up is a story.' },
    pause: 'Pause the motion',
    resume: 'Resume the motion',
    close: 'Close',
  },
  navigation: {
    signIn: 'Sign in',
    signOut: 'Sign out',
    signOutPartial: 'Signed out here, but other devices may still be signed in.',
    settings: 'Settings',
    more: 'More',
    closeMore: 'Close',
    back: 'Back',
    // Short on purpose: the measure is one slot of the bottom bar at 375px, so no article.
    // See apps/frontend/docs/menu-quattro-accessi.md.
    menu: {
      staff: 'Staff',
      players: 'Players',
      pitches: 'Pitches',
      teams: 'Squads',
      leagues: 'Championships',
      requests: 'Requests',
      subscriptions: 'Plan',
      matches: 'Matches',
    },
  },
  userMenu: {
    open: 'Your account, {account}',
    devBypass: 'Development bypass',
    // The phone's accounts (spec #278, #302): one in use, the others a tap away, with no password.
    onThisPhone: 'On this phone',
    addAccount: 'Add an account',
    signOutOf: 'Sign out of {name}',
    signOutAll: 'Sign out of every account',
    switchFailed: 'That account is no longer signed in here: sign in with it again.',
  },
  settings: {
    title: 'Settings',
    description: 'Your access and your contact details.',
    devBypassHint:
      'This session has no Cognito identity, so there is nothing to sign out of and no password to change.',
    accessTitle: 'Your access',
    usernameLabel: 'username',
    usernameHint: 'You sign in with it: your guardian chose it, and changes it with the password.',
    addEmailTitle: 'An e-mail of your own',
    addEmailDescription:
      'If you have an e-mail of your own, add it: from then on you sign in with it, and the password is yours. Your guardian is told.',
    emailLabel: 'e-mail',
    // Said because the field is read-only and the reason is not visible from the page.
    emailHint: 'It is the address you sign in with. To change it, write to us.',
    contactsTitle: 'Your contact details',
    contactsDescription: 'The number your club reaches you on.',
    phoneLabel: 'phone',
    save: 'Save',
    saved: 'Contact details updated.',
    reservedHint:
      'Name, date of birth and codice fiscale are what your club registered you with: they correct them.',
    // A parent signs up alone, so no club registered them and none corrects them (VOL-97).
    reservedHintWithoutClub:
      'Name, date of birth and codice fiscale cannot be changed here: to correct them, write to us.',
  },
  signIn: {
    title: 'Sign in',
    description: 'Enter your e-mail, or the username your guardian chose for you.',
    // One field for both: a child with no mail of their own signs in with a username (#294).
    email: 'E-mail or username',
    password: 'Password',
    submit: 'Sign in',
    forgotPassword: 'I forgot my password',
    registerClub: 'Register your club',
    // The hosted-UI pair: the social buttons left the screen, /callback did not leave the repo.
    pending: 'Signing in…',
    failed: 'Sign-in failed. Try again.',
    // Why the user is on this page, keyed by the `reason` search param: an expired session
    // and a first visit would otherwise render the same page.
    reason: {
      'session-expired': 'Your session has expired. Sign in again to continue.',
      required: 'That page needs you to be signed in.',
      'account-created': 'Your account is ready: sign in with your e-mail and your password.',
    },
    // «Add an account» from the menu (#302): the account in use stays on this phone.
    add: {
      title: 'Add an account',
      description:
        'Sign in with another account: the one you are using stays on this phone, and you pass from one to the other from the menu.',
      back: 'Back',
    },
    onThisPhone: 'Already on this phone',
    continueAs: 'Continue as {name}',
  },
  // The registration is not built: the page says so rather than the button lying about it.
  signUp: {
    title: 'Creating an account',
    description: 'Register the club you represent.',
    backToSignIn: 'Back to the sign-in',
    choose: {
      title: 'Who are you?',
      club: {
        title: 'A club',
        body: 'You represent a società and want to bring it onto Volée.',
      },
      player: {
        title: 'A player',
        body: 'You play for a club that is already on Volée.',
      },
      parent: {
        title: 'A parent, or a guardian',
        body: 'You create your account and add your children whenever you like.',
      },
    },
    // The sheet of a parent who registers alone: no club, no child.
    parent: {
      title: 'Your account',
      description: 'A few details are enough. You add your children later, from your page.',
      firstName: 'your first name',
      lastName: 'your last name',
      email: 'your e-mail',
      confirmEmail: 'confirm your e-mail',
      phone: 'your phone number',
      required: 'Required field.',
      emailMismatch: 'The e-mails do not match.',
      noticeTitle: 'The privacy notice',
      agree:
        'I have read the privacy notice and consent to the processing of my data to create the account.',
      agreeRequired: 'Tick the consent to create the account.',
      noticeLoading: 'Loading the privacy notice…',
      noticeError: 'The privacy notice would not load: try again in a moment.',
      submit: 'Create the account',
      submitting: 'Creating the account…',
      signIn: 'Go to sign-in',
    },
  },

  registration: {
    club: {
      title: 'Your club',
      description:
        'Pick the club you represent, or filter the list by name. Players and parents join with an invitation or a link from their own club.',
      field: 'club',
      placeholder: 'Filter by name',
      searching: 'Loading the clubs…',
      empty: 'No club by that name.',
      failed: 'The search did not work.',
      retry: 'Try again',
      change: 'change',
      missingEyebrow: 'CANNOT FIND IT?',
      missingBody: 'If your club is not listed, write to us: we will add it.',
      contact: 'Contact Volée',
      submit: 'Continue',
    },
    contact: {
      title: 'Contact Volée',
      description: 'Tell us which club is yours and we will get back to you to add it.',
      clubName: 'club name',
      email: 'e-mail',
      phone: 'phone',
      submit: 'Send the request',
      sent: 'Request sent. Volée replies within 48 hours or so.',
      back: 'Back to the search',
    },
    details: {
      title: 'Your details',
      description: 'Who you are. We need them to head the request and to reach you.',
      firstName: 'first name',
      lastName: 'last name',
      email: 'e-mail',
      phone: 'phone',
      required: 'Required field.',
      submit: 'Continue',
    },
    credentials: {
      title: 'Credentials',
      description: 'Choose how you will sign in to Volée.',
      submit: 'Continue',
      takenTitle: 'That e-mail is already registered',
      takenBody:
        'An account already exists for that address. Sign in with it, or go back and use another.',
    },
    document: {
      title: 'The document',
      description:
        'Upload a document proving your role in the club. A Volée operator reads it in the backoffice.',
      empty: 'No document uploaded.',
      choose: 'Choose a file',
      uploaded: 'uploaded',
      replace: 'Replace',
      remove: 'Remove',
      failedTitle: 'Upload did not work',
      failedBody: 'The file did not arrive. Try again.',
      retry: 'Try again',
      submit: 'Continue',
    },
    consents: {
      title: 'Privacy and consents',
      description: 'What Volée does with the data you have entered.',
      // DRAFT: the wording is the privacy consultant's to write. The three purposes and
      // which one is required are the structure, and that part is a product decision.
      noticeEyebrow: 'THE NOTICE',
      noticeBody:
        'Volée Football S.r.l. processes the data in this request to assess it and, if approved, to give you access to the platform as an official of your club. The document you upload is read by a Volée operator and kept as evidence of the check. You may ask at any time to see, correct or delete your data by writing to privacy@volee-football.com.',
      noticeDraft: 'Draft: the wording is being reviewed by the privacy consultant.',
      consentsEyebrow: 'THE CONSENTS',
      required:
        'I have read the notice and consent to my data being processed to assess this request.',
      requiredHint: 'Without this the request cannot be assessed.',
      contact: 'Volée may contact me by e-mail or phone about this request.',
      updates: 'Volée may write to me about new platform features. Optional.',
      missing: 'The first consent is required to continue.',
      submit: 'Continue',
    },
    review: {
      title: 'Summary',
      description: 'Check and send. After sending, the request goes to Volée.',
      clubGroup: 'THE CLUB',
      detailsGroup: 'YOUR DETAILS',
      accessGroup: 'ACCESS AND DOCUMENT',
      edit: 'edit',
      fullName: 'name and surname',
      email: 'e-mail',
      phone: 'phone',
      access: 'how you will sign in',
      accessPassword: 'password',
      document: 'document',
      consentsPending: 'Whether the consents are repeated here or stay on step 5.',
      submit: 'Send the request',
      failedTitle: 'Sending did not work',
      failedBody:
        'No connection. What you filled in is still here: try again when you are back online.',
      retry: 'Send again',
    },
    done: {
      title: 'Request sent',
      badge: 'awaiting approval',
      body: 'Your profile is awaiting approval. A Volée operator checks the request and the document: the answer comes within 48 hours or so.',
      backToSignIn: 'Back to the sign-in',
    },
    status: {
      eyebrow: 'YOUR REQUEST',
      loading: 'Loading your request…',
      failed: 'We could not read your request.',
      submittedTitle: 'Awaiting approval',
      submittedBadge: 'awaiting approval',
      submittedBody:
        'The request reached Volée. An operator is checking it together with the document: the answer comes within 48 hours or so.',
      receivedTitle: 'Request received',
      receivedNote: 'everything needed has arrived',
      reviewTitle: 'Under review',
      reviewNote: 'answer within 48 hours or so',
      integrationTitle: 'Something is missing',
      integrationBadge: 'something is missing',
      integrationBody:
        'The review stopped on one point. Once that is sorted, the request goes back in the queue.',
      reasonTitle: 'What the operator wrote',
      integrationUpload: 'Upload another document',
      rejectedTitle: 'Request not accepted',
      rejectedBadge: 'not accepted',
      rejectedBody: 'Volée reviewed the request and cannot accept it.',
      approvedTitle: 'Approved',
      approvedBadge: 'approved',
      approvedBody:
        'Your profile is active. From here you enter Volée as an official of your club.',
      approvedEnter: 'Enter Volée',
    },
  },

  newPassword: {
    title: 'Choose your password',
    description: 'The temporary password has to be replaced before you can continue.',
    submit: 'Save and continue',
    saved: 'Password set.',
  },
  // The second factor: the sign-in step, and the card that switches it on. TOTP only, and
  // optional — nobody is forced onto it. Why: docs/infra.md.
  mfa: {
    title: 'Enter the code from your app',
    description: 'Open your authenticator app and type the code it shows for Volée.',
    code: 'Code',
    submit: 'Sign in',
    cardTitle: 'Two-step verification',
    cardDescription: 'A code from an app on your phone, asked on top of your password.',
    statusOn: 'Two-step verification is on.',
    statusOff: 'Two-step verification is off.',
    lostDeviceTitle: 'If you lose the phone',
    lostDevice:
      'Keep the app on a phone you keep. If you lose it you cannot sign in on your own: write to us and we will switch it off for you.',
    enable: 'Turn on two-step verification',
    disable: 'Turn off',
    setupTitle: 'Scan the code',
    setupDescription:
      'Scan this with your authenticator app, or type the key into it by hand. Then enter the code the app shows.',
    secret: 'Key',
    copySecret: 'Copy the key',
    secretCopied: 'Key copied',
    unreadable: 'Cannot tell right now whether two-step verification is on. Try again later.',
    confirm: 'Confirm and turn on',
    cancel: 'Cancel',
    enabled: 'Two-step verification is on.',
    disabled: 'Two-step verification is off.',
  },
  // Adding a mail to an account that has none, proved with a code: reused wherever one is asked for.
  addEmail: {
    email: 'Your e-mail',
    emailHint: 'It must be yours: from then on you sign in with it.',
    confirmEmail: 'Repeat the e-mail',
    mismatch: 'The two e-mails differ.',
    required: 'Required.',
    send: 'Send the code',
    codeSent: 'We sent a code to {email}: type it here. It lasts one hour.',
    code: 'Code',
    confirm: 'Confirm the e-mail',
    otherEmail: 'Use another e-mail',
    done: 'Your e-mail is confirmed: from now on you sign in with {email}.',
  },
  passwordReset: {
    title: 'Reset your password',
    // Says nothing about whether the account exists: the page must not answer that question.
    description: 'Enter your email. If an account exists, a code arrives by email.',
    email: 'Email',
    request: 'Send the code',
    codeTitle: 'Enter the code',
    codeDescription:
      'If the address has an account, a code is on its way (to your guardians too, under 14): it lasts one hour. Enter it and choose a new password.',
    code: 'Code',
    confirm: 'Save the new password',
    done: 'Password updated.',
    signInYourself: 'Sign in with the new password.',
    backToSignIn: 'Back to sign-in',
    identifier: 'E-mail or username',
    toldTitle: 'We wrote to your guardians',
    toldDescription:
      'If «{username}» exists and you are under 14, your guardians with an account have received an e-mail: one of them chooses the new password, from their page.',
    forgotUsernameTitle: 'Do not remember the username either?',
    forgotUsernameBody: 'Ask your guardian: they see it on your card, on their page.',
    limit: 'At most 3 requests an hour.',
    noGuardian:
      'Already 14, or none of your guardians has a Volée account any more? Recovery goes through the Volée staff: ask your club to write to us.',
  },
  password: {
    new: 'New password',
    confirm: 'Confirm the new password',
    show: 'Show the password',
    hide: 'Hide the password',
    // Mirrors the pool's policy through @volee/config: a hint, never a validator.
    policyHint:
      'At least {minLength} characters, with an uppercase letter, a lowercase letter and a digit.',
    mismatch: 'The two passwords do not match.',
    policy: 'This password does not meet the requirements above.',
    required: 'Required field.',
    // Ticked off while someone types, so an unmet rule reads as «not yet», not as a failure.
    rules: 'Password requirements',
    ruleMinLength: 'At least {minLength} characters',
    ruleUppercase: 'One uppercase letter',
    ruleLowercase: 'One lowercase letter',
    ruleDigit: 'One digit',
    current: 'Current password',
    changeTitle: 'Change the password',
    changeDescription: 'You stay signed in here; your other devices are unaffected.',
    changeSubmit: 'Change the password',
    changed: 'Password changed.',
  },
  // The vocabulary of a block the design has not settled yet, for @volee/ui's Placeholder.
  placeholder: {
    blocked: 'BLOCKED',
    toConfirm: 'TO CONFIRM',
    mockData: 'FAKE DATA',
    mockValue: 'FAKE',
  },
  // The landing page of an invitation link, before any password is typed.
  activation: {
    title: 'Welcome to Volée',
    description:
      'Your club has been activated. Create your access here: we have already loaded the club’s data, you only have to check it.',
    club: 'your club',
    invitedPerson: 'invitation addressed to',
    stepsTitle: 'THREE STEPS AND YOU ARE IN',
    stepPassword: '— Create the password',
    stepDetails: '— Confirm your details',
    stepClubData: '— Check the club’s data',
    start: 'Let’s begin',
    notMe: 'This is not me',
  },
  // The three steps of the wizard. One POST at the end, so the first two only collect.
  wizard: {
    back: '← back',
    // `{step}` of `{total}`: the bar reads the same on all three, so one string covers them.
    progress: 'step {step} of {total}',
    password: {
      title: 'Create the password',
      email: 'e-mail',
      emailHint: 'From the invitation: it is the proof you own the address.',
      consent:
        'I accept the terms of use and I have read the privacy notice. The exact wording is the privacy consultant’s to write: this is where the club accepts what it accepts on entering the platform.',
      consentRequired: 'You have to accept in order to continue.',
      consentPending:
        'What the club accepts and in which words. It is the board’s «talk to the DPO» item, attached to exactly this step.',
      submit: 'Create the access',
    },
    profile: {
      title: 'Your details',
      description:
        'Who you are and with what role you are entering. The role determines what you can do on the platform.',
      firstName: 'first name',
      lastName: 'last name',
      nameRequired: 'Required field.',
      email: 'e-mail',
      phone: 'phone',
      phoneRequired: 'Required field.',
      birthDate: 'date of birth',
      birthDateRequired: 'Required field.',
      birthDateFuture: 'That date has not happened yet.',
      role: 'your role in the club',
      submit: 'Next',
    },
    review: {
      title: 'Check your details',
      description:
        'We filled these in during the activation. Take a look: if everything is right, confirm.',
      club: 'THE CLUB',
      clubReport: 'report',
      legalName: 'registered name',
      legalForm: 'legal form',
      registeredOffice: 'registered office',
      taxCode: 'tax code or VAT number',
      pec: 'certified email',
      legalRepresentative: 'legal representative',
      contact: 'THE CONTACT',
      contactEdit: 'edit',
      contactName: 'name and surname',
      contactPhone: 'phone',
      contactEmail: 'e-mail',
      // A statement and never a dash: `—` reads as «empty», and here the model is what
      // is empty, not the datum.
      missingValue: 'Not in the register yet',
      contractNote:
        'The registered office and VAT number are contract data: changing them takes a report, they are not corrected from here.',
      confirm: 'I confirm the data is correct',
      confirmRequired: 'Confirm the data before entering.',
      enter: 'Enter the platform',
      reportError: 'Report an error',
    },
    edit: {
      cancel: '← cancel',
      title: 'The contact',
      description:
        'One screen, reused for every group: the title changes and the fields change. Four or five fields at a time, not seventeen.',
      save: 'Save',
      cancelAction: 'Cancel',
      pending:
        'Which groups the club can correct by itself. The registered office and VAT number change the contract: the proposal is that they go through a report, not through this screen.',
      saved: 'Details updated.',
    },
    report: {
      title: 'What does not add up',
      description:
        'We take care of it and get back to you. In the meantime you can enter the platform: a report does not block access.',
      subject: 'WHICH DATA',
      subjectClub: 'The club’s data',
      subjectPitch: 'The pitch',
      subjectInfrastructure: 'The detected infrastructure',
      subjectContact: 'The contact',
      subjectRequired: 'Choose what the report is about.',
      description_: 'what is wrong',
      descriptionRequired: 'Describe what is wrong.',
      submit: 'Send the report',
      pending: 'Who receives the report and through which channel.',
      noteTitle: 'A report does not block access',
      noteBody:
        'Wrong data in the register does not stop you uploading squads or watching matches: the club enters and we correct it.',
      sent: 'Report sent.',
    },
  },
  // The invitation itself: the four ways a link can fail, and the two openings.
  invitation: {
    signedIn: {
      title: 'You opened the invitation with a session already active',
      otherAddress:
        'The invitation is for {invitedEmail}, while in this app you are signed in as {currentEmail}. Accepting it from here would change the account you are working with: to go on you have to sign out, then open the invitation again.',
      invitedAddressLabel: 'invitation addressed to',
      currentAddressLabel: 'in this app you are',
      signOut: 'Sign out and go back to the invitation',
      decline: 'Decline the invitation',
      notNow: 'Not now, back to Volée',
    },
    expired: {
      title: 'This invitation has expired',
      description:
        'Invitations have a lifetime: that is our choice, not your mistake. Ask for a new one and it arrives at the same address.',
      askNew: 'Ask for a new invitation',
      pending:
        'How long an invitation lasts, and who receives the renewal request: the club’s Volée contact or a support address?',
    },
    used: {
      title: 'This invitation has already been used',
      description:
        'An access created with this invitation already exists. The most common case is that you created it and do not remember.',
      signIn: 'Sign in with your credentials',
      forgotPassword: 'I forgot my password',
      notMe: 'It was not me',
      pending:
        'If someone else used the invitation it is a security problem, not a hiccup: what happens when somebody reports it?',
    },
    revoked: {
      title: 'This invitation is no longer valid',
      description:
        'Volée cancelled it. It happens when it was sent to the wrong address, or when the club’s contact person has changed.',
      pending:
        'Which channel: the contact’s phone, a support address, or a form? The board only says «contact volee».',
    },
    unknown: {
      title: 'This link is not valid',
      description:
        'The address you opened is not an invitation we recognise. Check the link in the message you received, or ask for a new one.',
    },
    contact: 'Contact Volée',
    staff: {
      title: 'You have been invited',
      description:
        'The club is giving you access to the platform. Who invited you and with what role is below: if it does not add up, do not accept.',
      club: 'the club inviting you',
      invitedBy: 'who invited you',
      role: 'your role',
      teams: 'the squads you will follow',
      noTeams: 'None: a club-wide role',
      noTeamYet: 'None yet: the club has not assigned one',
      accept: 'Accept the appointment',
    },
    // The `sign_in` path with nobody signed in: the address already has an account.
    signIn: {
      title: 'You already have a Volée account: sign in to accept the appointment',
      description: '{club} has invited you as {role}.',
      account: 'your account',
      action: 'Sign in',
      notYou: 'Not you? Ask whoever invited you to use another address.',
    },
    // The same path signed in, for an account that has no person behind it yet.
    accept: {
      namesTitle: 'Your details',
    },
  },
  // «Thursday 29 October at 18:40»: until when a link for the guardian works, the same on every page.
  deadline: {
    at: '{day} at {time}',
  },
  // The tutore's page at /tutore?token=: the child first, then who confirms (spec #278).
  guardianPage: {
    eyebrow: 'Guardian’s confirmation',
    heroTitle: '{name} wants to join Volée',
    heroBody:
      '{name} is {age}: under 14 the law asks for the consent of whoever holds parental responsibility. Then {club} decides.',
    validUntil: 'Valid until {deadline}',
    born: 'Born on',
    club: 'Club',
    team: 'Leva',
    noTeam: 'No leva yet',
    continue: 'Continue',
    decline: 'I do not confirm',
    whoTitle: 'Who you are',
    whoDescription:
      'We create an account for you: you need it to confirm and decide for your child.',
    haveAccount: 'Already have a Volée account? Sign in',
    relationship: 'Who are you to {name}?',
    relationships: { mother: 'Mother', father: 'Father', guardian: 'Legal guardian' },
    firstName: 'First name',
    lastName: 'Surname',
    phone: 'Phone',
    email: 'Your e-mail',
    emailHint: 'You need it to sign in to Volée.',
    password: 'Password',
    agree:
      'I am the parent or legal guardian of {name} and consent to their registration. I have read the privacy notice.',
    agreeRequired: 'Tick the consent to confirm.',
    required: 'Required.',
    noticeTitle: 'The privacy notice',
    noticeLoading: 'Loading the privacy notice…',
    noticeError: 'The privacy notice would not load: try again in a moment.',
    confirm: 'Confirm',
    confirming: 'Confirming…',
    signInTitle: 'Sign in',
    signInDescription:
      'Confirm with the account you already have: as a club official or a coach too.',
    signInConfirm: 'Sign in and confirm',
    signedInAs: 'You are signed in as {email}.',
    otherAccount: 'Sign in with another account',
    challenge:
      'This account asks for one more step to sign in: sign in from the login page, then open the link again.',
    toSignIn: 'Go to the login',
    refuseTitle: 'You do not confirm?',
    refuseCalloutTitle: '{name}’s request closes',
    refuseCalloutBody:
      '{club} will not be able to approve it, and {name} will see it on their page. If the link reached you by mistake, you can also just close this page.',
    refuseConfirm: 'I do not confirm',
    refuseBack: 'Go back',
    refusedTitle: 'All right, no confirmation',
    refusedBody:
      '{name}’s request is closed. If you change your mind, {name} can start again and send you a new link.',
    doneBadge: 'Confirmed',
    doneTitle: 'Done, thank you',
    doneBody:
      'Now {club} decides: we will write to you when it approves. From today you find {name} on your page.',
    childLoginTitle: '{name} signs in with their own e-mail',
    childLoginBody: 'And with the password they chose.',
    goToPage: 'Go to your page',
    expiredBadge: 'Expired',
    expiredTitle: 'This link has expired',
    expiredBody:
      'The link was valid until {deadline}. Ask {name} to send you a new one from their page: it lasts another 7 days.',
    confirmedBadge: 'Already confirmed',
    confirmedTitle: 'Somebody already took care of it',
    confirmedBody:
      '{who} already confirmed {name}’s registration. If you are their guardian too, ask for the link for another guardian: whoever confirmed sends it, from «Another guardian» on {name}’s card.',
    closedBadge: 'Closed',
    closedTitle: 'This request is closed',
    closedBody:
      'There is nothing left to confirm. If needed, {name} can start again and send you a new link.',
    notFoundTitle: 'This link does not exist',
    notFoundBody: 'Ask whoever sent it to send it again.',
    childDetail: '{age} years old · {club}',
    next: 'Next',
    signInNext: 'Sign in',
    accessTitle: '{name}’s access',
    accessDescription:
      '{name} has no e-mail: they will sign in with a username and the password you choose. Only you can change them, from your page.',
    username: 'Username',
    usernameHint:
      'We propose it, and you can change it. Letters, digits and dots. It is not public: only you and {name} see it.',
    usernameRules: 'Whether the username is free',
    usernameFree: '{username} is free',
    usernameTaken: '{username} is taken',
    proposalError: 'We could not propose a username: type one yourself.',
    childPassword: '{name}’s password',
    childPasswordConfirm: 'Repeat the password',
    childLoginUsernameTitle: '{name} signs in with «{username}»',
    childLoginUsernameBody: 'And with the password you chose: tell them yourself.',
    noClub: 'Not registered yet',
    childDetailNoClub: '{age} years old',
    // The link for one more guardian (#297): named by the child, sent by a guardian of theirs.
    another: {
      eyebrow: 'Another guardian',
      heroTitle: 'Become a guardian of {name}',
      heroBody:
        '{name} is {age}: under 14, consent is given by whoever holds parental responsibility. With your own account you become one of their guardians, with the same say as the others. None of you can remove another.',
      invitedBy: '{who} sent it to you.',
      agree: 'I am a parent or the legal guardian of {name}. I have read the privacy notice.',
      refuseCalloutTitle: 'You do not become a guardian of {name}',
      refuseCalloutBody:
        'The link closes. If it reached you by mistake, you can also just close this page.',
      refusedBody:
        'The link is closed. If you change your mind, ask whoever sent it for a new one.',
      doneTitle: 'Done: you are their guardian',
      doneBody:
        'From today you find {name} on your page, with the same say as the other guardians.',
      expiredBody:
        'The link worked until {deadline}. Ask whoever sent it for a new one: it lasts another 7 days.',
      confirmedTitle: 'This link has been used already',
      confirmedBody:
        '{who} has used it already. If you are a guardian of {name} too, ask a guardian of theirs for a new link from «Another guardian».',
      closedTitle: 'This link asks nothing any more',
      closedBody: 'There is nothing left to confirm for {name}.',
    },
  },
  // Who confirmed, the same words on the tutore's page, the waiting page and the club's queue.
  guardianConfirmation: {
    relationship: { mother: 'mother', father: 'father', guardian: 'legal guardian' },
    who: '{name} ({relationship})',
    confirmedOn: 'confirmed on {date} at {time}',
  },
  // The public flow a player opens from a club's own enrolment link, no session or invitation:
  // one branch at every age, and `RosterMatchRead.under_guardian_age` names the child or not.
  enrolment: {
    title: 'Enrol a player',
    invalidTitle: 'This link is not valid',
    invalidDescription: 'Ask the person who sent it for a new one.',
    progress: 'step {step} of {total}',
    pickClub: {
      title: 'Your club',
      description: 'Find the club you, or your child, play for.',
      start: 'Let’s start',
    },
    search: {
      title: 'Find your player',
      description: 'A few details and we check whether they are already on the roster.',
      firstName: 'first name',
      lastName: 'last name',
      birthDate: 'date of birth',
      firstNameRequired: 'Required field.',
      lastNameRequired: 'Required field.',
      birthDateRequired: 'Required field.',
      submit: 'Search',
      searching: 'Searching…',
      failed: 'The search did not work.',
      retry: 'Try again',
    },
    result: {
      matchedTitle: 'Player found',
      matchedDescription:
        '{name} is already on the club’s roster but has no sign-in yet: carry on to give them one. The club will approve the request.',
      continue: 'Continue',
      searchAgain: '← search again',
    },
    alreadyRostered: {
      title: 'This player is already with the club',
      body: '{name} is already on the club’s roster, so there is no need to enrol them again. If you think this is a mistake, contact the club.',
      searchAgain: 'Search again',
    },
    playerData: {
      title: '{name}’s details',
      description: 'Check the details and fill in what is missing: the tesseramento needs them.',
      recapLabel: 'What you searched for',
      birthLine: 'Date of birth: {date}',
      edit: 'Edit',
      sex: 'Sex',
      sexMale: 'Male',
      sexFemale: 'Female',
      sexRequired: 'Choose the sex.',
      taxCode: 'Codice fiscale',
      taxCodeHint: 'Sixteen characters, as on the health card.',
      taxCodeRequired: 'Type the codice fiscale.',
      taxCodeFormat: 'The codice fiscale has sixteen characters, letters and numbers.',
      missing: 'Choose the sex and type the codice fiscale to continue.',
      missingTaxCode: 'Type the codice fiscale to continue.',
      submit: 'Continue',
    },
    consents: {
      title: 'Privacy and consents',
      noticeTitle: 'The notice',
      agree:
        'I have read the notice and I consent to the processing of my own data and {name}’s for the enrolment with the club.',
      agreeRequired: 'Tick the consent to send the request.',
      noticeLoading: 'Loading the notice…',
      noticeError: 'The notice did not load: try again shortly.',
      next: 'Continue',
      submit: 'Send the request',
      submitting: 'Sending…',
    },
    // Under 14 the access is still the child's own: their address, their password (#278).
    childDetails: {
      title: 'How {name} will sign in',
      description:
        'With their own e-mail, if they have one. Otherwise their guardian makes the access.',
      email: '{name}’s e-mail',
      emailHint: 'It must be theirs, not the guardian’s.',
      confirmEmail: 'repeat the e-mail',
      phone: 'their phone',
      hasEmail: 'Does {name} have an e-mail of their own?',
      hasEmailYes: 'Yes',
      hasEmailNo: 'No',
      helpTitle: 'Ask a parent for help',
      helpBody:
        'Without an e-mail, {name}’s access is made by their guardian on confirming: they choose the username and the password, and only they can change them.',
    },
    guardianLink: {
      title: 'Who confirms for {name}?',
      description: '{name} is under 14: a parent or guardian has to say yes.',
      here: { title: 'They are here with me', detail: 'Confirm now, from this phone.' },
      send: {
        title: 'I’ll send them the link',
        detail: 'They get a link and confirm when they can, within 7 days.',
      },
      eyebrow: 'Link for the guardian',
      validUntil: 'Valid until {deadline}',
      share: 'Share',
      whatsApp: 'Send on WhatsApp',
      copy: 'Copy link',
      copied: 'Link copied',
      // The child speaks: the 14 is the guardian_threshold_age setting, and changes with it.
      message: 'Hi! To join Volée I need your confirmation, because I am under 14: {link}',
      done: 'Done, I’ll wait',
    },
    // «Add a child»: the guardian is already inside, so the yes is theirs as the request leaves.
    guardianConfirm: {
      title: 'Confirm for {name}',
      description:
        '{name} is under 14: a parent or guardian has to say yes. You are signed in with your own account, so you give it now.',
      submit: 'Confirm and send',
      submitting: 'Sending…',
    },
    playerDetails: {
      title: 'Your details',
      description: '{name} is 14 or over, so they submit the request themselves.',
      email: 'your e-mail',
      emailHint: 'The club replies to this address.',
      emailRequired: 'Required field.',
      confirmEmail: 'confirm your e-mail',
      confirmEmailRequired: 'Required field.',
      emailMismatch: 'The e-mail addresses do not match.',
      phone: 'your phone',
      alreadyThereTitle: 'You’re already one of us',
      inUseTitle: 'This e-mail already has an account',
      inUseBody:
        'We do not say whose. If it is yours, sign in. If you have no e-mail of your own, go on without: your guardian makes your access.',
      withoutMail: 'Go on without an e-mail',
      inUseForAChild: 'This e-mail already has an account: write one that is your child’s alone.',
      signIn: 'Go to sign in',
      submit: 'Continue',
    },
    review: {
      title: 'Check and send',
      description: 'Once sent, the request goes to the club.',
      playerGroup: 'THE PLAYER',
      requesterGroup: 'YOUR DETAILS',
      firstName: 'first name',
      lastName: 'last name',
      birthDate: 'date of birth',
      relationship: 'relationship',
      email: 'e-mail',
      phone: 'phone',
      edit: 'edit',
      submit: 'Send the request',
      failed: 'Sending did not work.',
      retry: 'Send again',
    },
    status: {
      eyebrow: 'THE REGISTRATION',
      loading: 'Loading the request…',
      failed: 'We could not read the request.',
      stagesEyebrow: '{name}’s registration',
      stagesLabel: 'The stages of the registration',
      stageDone: 'done',
      stageCurrent: 'in progress',
      stageTodo: 'to come',
      stageFailed: 'did not happen',
      stageSignUp: 'Registration',
      stageSignUpBody: 'Your account is ready.',
      stageSignUpNoAccessBody: 'Your request is on its way: your guardian makes your access.',
      stageSignUpOwnAccessBody: 'Your request is on its way.',
      stageGuardian: 'The guardian confirms',
      stageClub: 'Approval by {club}',
      stageGuardianDone: '{who} confirmed on {date}.',
      ownAccessTitle: 'From today, you decide',
      ownAccessStage: 'Your e-mail',
      ownAccessBody:
        'You have turned 14: your guardian is no longer needed. Add an e-mail of your own and choose a password: it is your access, and the request goes on to the club.',
      ownAccessConfirm: 'Confirm and enter',
      expiredTitle: 'The link has expired',
      expiredBody:
        'Your guardian did not confirm within 7 days. Send them a new one: it lasts another 7 days.',
      declinedTitle: 'Your guardian did not confirm',
      declinedBody:
        'The request has closed. If it was a mistake, talk to them and start again: they get a new link.',
      dataDeletedOn: 'If nobody confirms, your data are deleted on {date}.',
      sendAgain: 'Send a new link',
      startAgain: 'Start again',
      awaitingGuardianTitle: 'Waiting for your guardian',
      awaitingGuardianBody: 'Once they confirm, {club} sees your request and can approve it.',
      submittedTitle: 'Waiting for the club',
      submittedBody:
        '{club} checks the request and decides on {name}’s registration: we will write when it does.',
      reasonTitle: 'What the club wrote',
      rejectedTitle: 'Request not accepted',
      rejectedBadge: 'not accepted',
      rejectedBody: '{club} reviewed the request for {name} and cannot accept it.',
      approvedTitle: 'Registration approved',
      approvedBadge: 'approved',
      approvedBody: '{club} approved {name}’s registration. From here you enter Volée.',
      approvedEnter: 'Enter Volée',
    },
  },
  club: {
    title: 'Clubs',
    description: 'Protected area: visible only to the roles allowed in.',
    noSelection: 'No club selected.',
  },
  // The page of any club, `/clubs/$clubId`: its sections speak with the dashboard's words.
  clubPage: {
    notFound: 'This club does not exist.',
  },
  // The four profile pages, one per facet. `/players` keeps its own catalogue below: it is
  // §4.4's executable demonstration and lists placeholder people, not a profile.
  official: {
    back: 'dashboard',
    mock: 'No endpoint serves this data yet.',
    chooseClub: {
      title: 'Which club?',
      description: 'You hold an incarico in more than one. Pick the one you want to open.',
    },
    dashboard: {
      title: 'Your club',
      countPlayers: 'Players',
      countSquads: 'Squads',
      countPitches: 'Pitches',
      countUnavailable: 'Not available',
      // The hero's way to the kits page, and the invitation while there is no first kit.
      kitsLink: 'The kits',
      kitsInvitation: 'Which colours do you play in?',
      kitsInvitationDetail: 'Set them once: the call-up will suggest the right one.',
      kitsInvitationAction: 'Set the first kit',
      nextMatches: 'MATCH CALENDAR',
      noNextMatches: 'No match scheduled.',
      matchSummary: '{date} · {home} — {away}',
      squads: 'THE SQUADS',
      squadsCount: 'how many there are',
      allSquads: 'See every squad',
      // A squad card's place: alone when the squad stands in one girone, named when in more.
      squadPlace: 'Place {position}',
      squadPlaceIn: 'Place {position} · Group {code}',
      allMatches: 'See every fixture',
      allPlayers: 'See every athlete',
      organigramma: 'THE ORGANIGRAMMA',
      // Said to the dirigente, about the parents: a family reads the section without it.
      organigrammaNote:
        'Who is behind the club: to a parent weighing it up, that says more than a statistic.',
      officials: 'CLUB OFFICIALS',
      staffBySquad: 'STAFF BY SQUAD',
      waitingForSquad: 'Waiting for a squad',
      adults: '{count} adults',
      oneAdult: '1 adult',
      noCoach: 'No coach yet.',
      seasonCurrent: 'current season',
      seasonPrevious: 'previous season',
      pitches: 'THE PITCHES',
      noPitches: 'No pitch is linked to your club yet.',
      // The same, to a reader who is not of the club: «your club» would be someone else's.
      noPitchesOfThisClub: 'No pitch is linked to this club yet.',
      individual: 'INDIVIDUAL RECOGNITION',
      individualNote:
        'Athletes called up by professional clubs or selected for representative sides.',
      allDone: 'All set',
      noRecording: 'A match has no footage',
      noRecordingBody:
        'On which pitch, and since when. Matches played in these days may not have been filmed.',
      lastMatches: 'LAST MATCHES UPLOADED',
    },
    // I primi passi on the dashboard, in place of the kits invitation: one entry per voce, keyed
    // as `GET /v1/clubs/{id}/onboarding` names them.
    onboarding: {
      eyebrow: 'First steps',
      title: '{club} on the pitch',
      meter: '{done} of {total} steps done',
      stateDone: 'done',
      stateTodo: 'to do',
      stateBlocked: 'not yet',
      optional: '{title} · optional',
      teamsProgress: '{done} squads of {total}',
      oneTeamProgress: '1 squad of {total}',
      unassigned: '{count} players without a squad',
      oneUnassigned: '1 player without a squad',
      waitingForTeams: 'The squads come first.',
      // «Lo farò dopo» leaves the small card, and «Riprendi» brings the list back on every device.
      later: 'I’ll do it later',
      resume: 'Resume',
      next: 'Next: {step}',
      // Every voce that counts done: the card «in campo», whose closing is for every dirigente.
      complete: '{club} is on the pitch',
      completeEyebrow: 'First steps · {done} of {total}',
      completeDetail:
        'Squads, players, rosters, staff, families and kits: it is all there. From here the dashboard is yours.',
      completeMissingLogo: 'The crest is missing',
      close: 'Close',
      steps: {
        teams: {
          title: 'Your squads',
          description:
            'The squads from the LND groups are already here, entered by Volée. Check that none is missing, Pulcini and Esordienti included: for one that is missing, wrong or not yours, write to Volée.',
          action: 'Check the squads',
        },
        registrations: {
          title: 'The players',
          description:
            'Download the register from the LND portal and upload it: amateurs and youth sector, together too. Every player comes in with their federation number and tax code.',
          action: 'Upload the register',
        },
        rosters: {
          title: 'The rosters',
          description:
            'Every player in their squad. We suggest it from the year of birth: check and confirm.',
          action: 'Build the rosters',
          waitingForRegistrations: 'The players come first.',
        },
        staff: {
          title: 'The staff',
          description:
            'Put someone in the seats of every squad, the coach or the team official: they get an email, and the seat opens when they accept.',
          action: 'Invite the staff',
        },
        families: {
          title: 'The families',
          description:
            'Send the enrolment link to the WhatsApp group of every squad: the families find their children already registered.',
          action: 'Send the link',
          waitingForRegistrations:
            'The players come first: that way every family finds their child.',
        },
        kits: {
          title: 'The kits',
          description:
            'The first and the second kit: the call-up will suggest the right one, and the analysis recognises the squad.',
          action: 'Set the kits',
        },
        logo: {
          title: 'Crest and cover',
          description:
            'Optional: the crest and the photo of the club, on the dashboard and on the pages of the squads.',
          action: 'Upload the crest',
        },
      },
    },
    // The bar on the page a voce leads to: `steps` names each voce in one word, as «… · done» says it.
    onboardingBar: {
      label: 'First steps',
      position: 'Step {position} of {total}',
      optional: 'Optional',
      done: '{step} · done',
      back: 'First steps',
      next: 'Next',
      steps: {
        teams: 'Squads',
        registrations: 'Players',
        rosters: 'Rosters',
        staff: 'Staff',
        families: 'Families',
        kits: 'Kits',
        logo: 'Crest',
      },
    },
    people: {
      title: 'Staff',
      listTitle: 'PEOPLE',
      accounts: 'THE ACCOUNTS',
      accountsManagement: '{count} management',
      accountsSquad: '{count} squad',
      accountsCounted: 'Only active accounts count: an invitation counts once accepted.',
      filterAll: 'everyone',
      filterAdmin: 'who administers',
      filterCoach: 'who is on the bench',
      hasAccount: 'active',
      pendingTitle: 'Pending invitations',
      pendingState: 'Pending',
      expiredState: 'Expired',
      offerExpires: 'expires on {date}',
      offerExpired: 'expired on {date}',
      resend: 'Resend',
      resent: 'Invitation sent again to {email}',
      resentNotEmailed: 'Invitation resent, but the email did not go out: try again later.',
      revoke: 'Revoke',
      revokeTitle: 'Revoke the invitation to {email}?',
      revokeBody:
        'The link will stop working. The invitation has not given any role yet: you can invite the same address again whenever you like.',
      revoked: 'Invitation revoked',
      unseatedTitle: 'Squads with nobody in their seats',
      unseatedBody:
        '{squads}. Until someone holds a seat there, you handle their rosa, match squads and kits yourselves.',
      invite: 'Invite someone',
      empty: 'Nobody holds an incarico here yet.',
    },
    invite: {
      title: 'Invite someone',
      description: "They'll accept from a link and fill in their details.",
      close: 'Close',
      email: 'Email',
      emailNote: 'The invitation goes here, and it is what proves the address is theirs.',
      emailOfTheHolder:
        'The address the club has for this person: the invitation gives them the account.',
      titleEntry: 'Invite to sign in',
      descriptionEntry:
        '{name} holds the seat already: the invitation gives them the account to sign in with, and takes no other seat.',
      role: 'Role',
      roleOfficial: 'Club official',
      roleCoach: 'Allenatore',
      descriptionOnto:
        'Onto a seat of the squad {squad}. They’ll accept from a link and fill in their details.',
      officialsFull: 'The club seats are taken: end a club official’s incarico to invite another.',
      squadsFull:
        'Every squad’s seats are taken: remove someone from a squad to invite a coach or a team official.',
      team: 'Squad',
      pickTeam: 'Choose the squad',
      teamNote: 'You invite onto a free seat only: these are the squads that have one.',
      submit: 'Send the invitation',
      sending: 'Sending…',
      cancel: 'Cancel',
      sent: 'Invitation sent to {email}',
      notEmailed: 'Invitation created, but the email did not go out: resend it from the list.',
    },
    person: {
      back: 'the people',
      data: 'THEIR DETAILS',
      heldIncarichi: 'ROLES AT THE CLUB',
      endIncarico: 'end it',
      endTeamIncarico: 'end role',
      endTitle: 'End {name}’s incarico as {role}?',
      endTeamTitle: 'End {name}’s incarico as {role} for the squad {squad}?',
      endBody: 'If it is their last incarico, they are no longer part of the società on Volée.',
      endYes: 'End it',
      removed: 'Incarico ended',
      addIncarico: 'ADD AN INCARICO',
      addNote: 'Assigning is not inviting: this gives a role, not an account.',
      officialsFull: 'The club seats are taken: end a club official’s incarico to give another.',
      team: 'Squad (optional)',
      noAvailableIncarico: 'All available appointments are already assigned or have no free seats.',
      teamNone: 'No squad yet',
      seatsFull: 'This squad’s seats are taken: free one before assigning another person.',
      seatHeld: 'This person already manages the squad: the new role uses the same seat.',
      officialNoSeat: 'This person already manages the squad through their club access.',
      seatMark: 'Team seat',
      addSubmit: 'Add the incarico',
      adding: 'Adding…',
      added: 'Incarico added',
      assignedSquads: 'ASSIGNED SQUADS',
      noSquadHere: 'No squad assigned',
      noAssignedSquad: 'Awaiting squad assignment',
      awaitingSquad: 'AWAITING SQUAD ASSIGNMENT',
      clubStaff: 'CLUB STAFF',
      awaitingSquadHelp:
        'Without a squad, this appointment stays on the club staff awaiting assignment and grants no squad management access.',
      unseatedSquads: 'OTHER SQUADS',
      remove: 'remove from squad',
      assign: 'assign',
      inChargeSince: 'in charge since',
      inChargeSinceSeason: 'in charge since this season {date}',
      fieldName: 'full name',
      fieldNameValue: 'from the invitation',
      fieldEmail: 'e-mail',
      fieldEmailHint: 'not editable: it is the account’s identity',
      fieldTaxCode: 'codice fiscale',
      fieldTaxCodeHint: 'corrected by Volée',
      fieldPhone: 'phone',
      actOnSquad: '{action} — {squad}',
      actOnIncarico: '{action} — {role} · {squad}',
      notFound: 'This person does not work with your club.',
      minorElsewhere:
        'You see this person’s name and role: they are a minor and play for another club.',
    },
    club: {
      title: 'Your club’s data',
      description: 'Check your details, and correct them.',
      back: 'your club',
      identity: 'THE CLUB',
      fieldLegalName: 'legal name',
      fieldShortName: 'short name',
      fieldAddress: 'registered address',
      fieldRegion: 'region',
      fieldProvince: 'province',
      chooseRegion: 'Choose a region',
      chooseProvince: 'Choose a province',
      chooseRegionFirst: 'Choose the region first',
      noProvince: 'Not recorded',
      noAddress: 'not recorded',
      voleeOwns:
        'The legal name, the short name and this address are ours to change: write to us and we will.',
      edit: 'Edit the data',
      save: 'Save',
      saved: 'Province saved.',
      crest: 'THE CREST',
      crestAlt: 'The crest of {club}',
      crestNote: 'It is what parents recognise you by, on every page of the product.',
      logoUpload: 'Choose a file',
      logoReplace: 'Replace',
      logoRemove: 'Remove',
      logoSaved: 'Crest saved.',
      logoRemoved: 'Crest removed.',
      logoTooLarge: 'The file is over {maxMb} MB.',
      cover: 'THE COVER',
      coverNote:
        'A landscape photo with its subject in the middle: your page cuts it itself, for a phone and for a computer, as you see here.',
      coverPhone: 'Phone',
      coverWide: 'Computer',
      coverUpload: 'Choose a photo',
      coverReplace: 'Replace',
      coverRemove: 'Remove',
      coverSaved: 'Cover saved.',
      coverRemoved: 'Cover removed.',
      coverTooLarge: 'Even shrunk, the photo is over {maxMb} MB: choose another one.',
      coverUnreadable: 'That file could not be opened as an image: choose another one.',
      notFound: 'This club does not exist.',
      noClub: 'You hold no incarico at a club, so there is nothing to show.',
      loadError: 'The club’s data could not be loaded.',
    },
    pitches: {
      title: 'Your pitches',
      list: 'THE PITCHES YOU PLAY ON',
      empty: 'No pitch is linked to your club.',
      listError: 'The pitches could not be loaded.',
      primary: 'primary',
      camera: 'Camera',
      cameraSince: 'since {date}',
      noCamera: 'no camera',
      software: 'Software',
      softwareDated: 'dated {date}',
      softwareUnknown: 'not reported yet',
    },
    players: {
      title: 'Players',
      addPlayer: 'Add player',
      searchLabel: 'Search by name',
      filters: 'Filters',
      clearFilters: 'Reset',
      resultCount: '{count} players',
      resultCountOne: '{count} player',
      groupTeam: 'Leva',
      allTeams: 'All',
      withoutTeam: 'No squad',
      groupPosition: 'Role',
      allPositions: 'All',
      groupYear: 'Birth year',
      allYears: 'All',
      groupAccount: 'Account',
      allAccounts: 'All',
      withAccount: 'With account',
      withoutAccount: 'Without account',
      groupStatus: 'Status',
      statusActive: 'Active',
      statusReleased: 'Released',
      statusAll: 'All',
      statusAllChip: 'Active and released',
      listLabel: 'The players roster',
      sortLabel: 'Sort by',
      sortLastNameAsc: 'Surname (A–Z)',
      sortLastNameDesc: 'Surname (Z–A)',
      sortBirthDateDesc: 'Youngest first',
      sortBirthDateAsc: 'Oldest first',
      empty: 'No player is tesserato with this società yet.',
      emptySearch: 'No player matches the search and the filters.',
      clearAll: 'Reset the search and the filters',
      loading: 'Loading the players…',
      listError: 'The players could not be loaded.',
      paginationLabel: 'pagination of the players',
      // Svincolo: the dirigente releases a tesserato of the società, and undoes a mistaken release.
      release: 'Release',
      releaseLabel: 'Release {name}',
      releaseTitle: 'Release {name}',
      releaseNote:
        'Leaves the rosa ({squad}) and the count of the tesserati; keeps account, profile and history. Nothing reaches the family.',
      releaseNoteWithoutSquad:
        'Leaves the count of the tesserati; keeps account, profile and history. Nothing reaches the family.',
      releaseDate: 'Date of the release',
      releaseDateHint: 'Today, or the day they left.',
      releaseConfirm: 'Release',
      releaseCancel: 'Cancel',
      releaseClose: 'Close',
      releaseDone: 'Release of {name} recorded.',
      releasedToday: 'Released today',
      releasedOnDay: 'Released on {date}',
      undoRelease: 'Undo',
      restore: 'Undo the release',
      restoreLabel: 'Undo the release of {name}',
      restoreDone: 'Release of {name} undone.',
      restoreRefused:
        'The release of {name} cannot be undone: there is already another tesseramento for this season.',
      unassigned: '{count} without a squad',
      unassignedDetail: 'We suggest the squad from the year of birth.',
      unassignedAction: 'Build the rosters',
      // No tesserato in the whole season: the ink card that leads to the tabulato.
      emptyCard: {
        title: 'No tesserato in the {season} season',
        titleWithoutSeason: 'No tesserato in this season',
        body: 'Upload the LND register: the tesserati all come in together, with their matricola and tax code. For a single case there is adding by hand.',
        upload: 'Upload the register',
        addByHand: 'Add by hand',
      },
    },
    // «Build the rosters»: the senza squadra of the season, each with the squad the annata proposes.
    leve: {
      title: 'Build the rosters',
      loading: 'Loading the tesserati without a squad…',
      listError: 'The tesserati without a squad could not be loaded.',
      season: 'Season {season}',
      heading: '{count} tesserati without a squad',
      headingOne: '1 tesserato without a squad',
      intro:
        'We suggest the squad from the year of birth. Where you have two squads for the same year, like the Pulcini A and B, you choose: {count} to choose.',
      introAllProposed: 'We suggest the squad from the year of birth: check, and confirm.',
      // Everyone has a squad: the card that leads out, to the squads or, from the guide, back to it.
      emptyCard: {
        title: 'No tesserato without a squad',
        body: 'Every tesserato of the season has a rosa. If more arrive, with the register or by hand, you will find them here.',
        toTeams: 'See the squads',
        toGuide: 'Back to the first steps',
      },
      annata: 'Born in {year}',
      seniores: '{year} and before',
      undated: 'No date of birth',
      chipToChoose: '{count} · to choose',
      chipSomeToChoose: '{count} · {open} to choose',
      chipOneSquad: '{count} · {squad}',
      chipSeveral: '{count} · suggested',
      everyone: 'All {count} in',
      everyonePicker: 'Squad of everyone born in {year}',
      everyoneUndatedPicker: 'Squad of everyone without a date of birth',
      picker: 'Squad of {name}',
      placeholder: 'Choose',
      none: 'No squad',
      proposed: 'suggested',
      female: 'F',
      noTeamForHer: 'no women’s squad for her',
      noTeamForHim: 'no men’s squad for him',
      noTeamForYear: 'no squad for the year',
      noBirthDate: 'no date of birth',
      noSex: 'no sex in the record',
      barLabel: 'Controls for building the rosters',
      status: '{assigned} of {total} in a squad',
      confirm: 'Confirm',
      done: '{count} tesserati in their rose.',
      doneOne: '1 tesserato in their rosa.',
    },
    requests: {
      title: 'The families’ requests',
      description:
        'Until a request is dealt with, that boy has a limited profile: he cannot see his own highlights.',
      empty: 'No request is waiting.',
      // The row's second line, R22's leva named last.
      rowDetail: 'born {birthDate} · {team}',
      matched: 'already on file',
      awaitingGuardian: 'Waiting for the guardian',
      awaitingOwnMail: 'Waiting for their e-mail',
      // The three groups of the queue (spec #278): to approve, waiting for the guardian, closed.
      groupToApprove: 'To approve',
      groupAwaitingOwnMail: 'Waiting for their e-mail',
      groupAwaitingOwnMailNote:
        'They turned 14: no guardian is needed any more, and the request is approved once they have an e-mail of their own.',
      groupAwaitingGuardian: 'Waiting for the guardian',
      groupAwaitingGuardianNote: 'Under 14, a request is approved after a guardian has confirmed.',
      groupClosed: 'Closed without confirmation',
      expired: 'Expired',
      expiredDetail: 'The guardian did not confirm within 7 days',
      declined: 'Not confirmed',
      declinedDetail: 'The guardian said no',
      linkExpires: 'The link expires {deadline}',
      waitsForOwnMail: 'Turned 14: no guardian needed any more',
      back: 'the requests',
      backToDetail: 'the request',
      notFound: 'This request no longer exists.',
      approve: 'Approve',
      reject: 'Reject',
      rejectTitle: 'Reject the request',
      rejectWarningTitle: 'Whoever made the request receives what you write',
      rejectWarningBody:
        'A reason is needed: without one, someone who got a detail wrong does not know how to put it right.',
      rejectMessage: 'the message they will receive',
      rejectSend: 'Send the rejection',
      sectionPlayer: 'Player',
      sectionConsent: 'Consent',
      sectionTeam: 'Leva',
      sex: 'Sex',
      taxCode: 'Codice fiscale',
      birthDate: 'Date of birth',
      age: '{age} years old',
      phone: 'Phone',
      email: 'Email',
      hasAccount: 'already has an account',
      alreadyOnFile: 'already on file',
      consentGiven: 'Given on {date} at {time} · notice version {version}',
      consentMissing: 'No consent on record (the request predates it)',
      noTeam: 'No leva',
      teamChange: 'Today they are in {current}: approving moves them to {chosen}',
      todayInTeam: 'Today: {team}',
      requestFor: 'Request for {team} · sent on {date}',
      requestSent: 'Request sent on {date}',
      declaration:
        'I confirm that the data I am approving, including the player’s and the guardians’, is correct and consistent with the documentation the club has on file.',
      declarationRequired: 'Tick the declaration to approve.',
      taxCodeToWrite: 'The player’s codice fiscale',
      taxCodeToWriteHint:
        'The request came without one and the anagrafica has none: nobody is registered without it. Sixteen characters, as on the health card.',
      taxCodeToWriteRequired: 'Type the codice fiscale to approve.',
      notYetTitle: 'It cannot be approved yet',
      notYetBody: 'They are under 14: the guardian has to confirm first, and has until {deadline}.',
      notYetBodyNoDeadline: 'They are under 14: the guardian has to confirm first.',
      notYetOwnAccess:
        'They have turned 14: approve once they have added an e-mail of their own from their request page.',
      closedOutcome: 'Closed without the guardian’s confirmation',
      declaredByFamily: 'declared by the family',
      approvedOutcome: 'Approved',
      approvedOn: 'on {date}',
      rejectedOutcome: 'Rejected',
      rejectedReason: 'Reason: {reason}',
      sectionGuardian: 'Guardian',
      guardianNotNeeded: 'Not needed: they are 14 or older',
      guardianPending: 'Not confirmed yet',
      rowConfirmedBy: 'Confirmed by {who}',
      correctBirthDate: 'Correct the date of birth',
      correctSave: 'Save the date',
      correctCancel: 'Cancel',
      corrected: 'Date of birth corrected.',
      correctedBackToGuardian:
        'Date corrected: under 14 the request waits for the guardian, and {name} finds a new link on their page.',
    },
    player: {
      notFound: 'This player does not play for your club.',
      listError: 'The player could not be loaded.',
      registrations: 'TESSERAMENTI',
      noRegistrations: 'No tesseramento is on file for this person.',
      active: 'active',
      releasedOn: 'released on {date}',
      noTeam: 'no team',
      teamsTitle: 'Squads — {club} — {season}',
      teamsSaved: 'Squads saved.',
      teamsSave: 'Save',
      teamsCancel: 'Cancel',
      teamsNone: 'No squad',
      teamsEmpty: 'This club has no squads yet.',
      teamsListError: 'The squads could not be loaded.',
      // The scheda's own casing: lowercase, like full name / e-mail / codice
      // fiscale / phone above it — apps/frontend/docs/quattro-profili-sotto-me.md.
      position: 'position',
      positionPlaceholder: 'No position assigned',
      foot: 'foot',
      footPlaceholder: 'Not declared',
      nationality: 'nationality',
      nationalityPlaceholder: 'Not declared',
    },
    newPlayer: {
      title: 'Add player',
      description:
        'Type the player’s details below. If this person is already on file, the server reuses them by name and date of birth — no search is needed.',
      close: 'Close',
      lastName: 'Surname',
      firstName: 'First name',
      birthDate: 'Date of birth',
      birthDateHelp: 'With the two names it identifies the person.',
      sex: 'Sex',
      sexPlaceholder: 'Choose one',
      taxCode: 'Codice fiscale',
      taxCodeHelp: 'Sixteen characters, as on the health card.',
      figcNumber: 'FIGC number',
      figcNumberHelp:
        'Optional. The one on the FIGC card: it stays the same if the player changes club.',
      position: 'Position',
      positionPlaceholder: 'Not recorded',
      phone: 'Phone',
      email: 'Email',
      submit: 'Register the player',
      submitting: 'Registering…',
      cancel: 'Cancel',
      required: 'Required',
      registered: '{name} registered.',
    },
    // The import of the tabulato: the files, what will happen, what happened.
    tabulato: {
      title: 'Upload the register',
      bar: 'Import commands',
      files: {
        eyebrow: 'Season {season}',
        upload: 'Upload the register',
        uploadNote:
          'The LND file as it is, whole. Several files will do too, amateurs and youth together: from the rows we work out which squads each one covers.',
        perSquad: 'Upload one file per squad',
        wholeTitle: 'The register',
        loaded: '{count} uploaded',
        loadedOne: '1 uploaded',
        addFile: 'Add a file',
        squadsTitle: 'One file per squad',
        squadsCovered: '{done} of {total} with a file',
        wholeAgain: 'All in one file',
        pick: 'Choose',
        pickFor: 'the file of {squad}',
        remove: 'Remove {file}',
        rows: '{count} rows',
        oneRow: '1 row',
        reading: 'Reading…',
        next: 'See who goes in',
      },
      // What the files cover, said of the squads: the server reads it from the rows (#405).
      cover: {
        kinds: {
          none: 'Type not recognised',
          dilettanti: 'Amateurs',
          settore_giovanile: 'Youth',
          both: 'Amateurs and youth',
        },
        squads: {
          seniores: {
            one: 'the Seniores',
            many: 'the Seniores',
            male: 'boys',
            female: 'girls',
            open: 'mixed',
          },
          juniores: {
            one: 'the Juniores',
            many: 'the Juniores',
            male: 'boys',
            female: 'girls',
            open: 'mixed',
          },
          allievi: {
            one: 'the Allievi',
            many: 'the Allievi',
            male: 'boys',
            female: 'girls',
            open: 'mixed',
          },
          giovanissimi: {
            one: 'the Giovanissimi',
            many: 'the Giovanissimi',
            male: 'boys',
            female: 'girls',
            open: 'mixed',
          },
          esordienti: {
            one: 'the Esordienti',
            many: 'the Esordienti',
            male: 'boys',
            female: 'girls',
            open: 'mixed',
          },
          pulcini: {
            one: 'the Pulcini',
            many: 'the Pulcini',
            male: 'boys',
            female: 'girls',
            open: 'mixed',
          },
        },
        covers: 'Covers {squads}.',
        coversNone: 'No squad of yours: the rows go in without a squad.',
        unassigned: 'Also {count} rows of {years}: no squad of yours, they go in without one.',
        unassignedOne: 'Also 1 row of {years}: no squad of yours, it goes in without one.',
        noTeams: 'You have no squads in Volée: the rows go in without a squad.',
        all: 'All {total} squads covered: we will tell you who is gone in each.',
        allOne: 'Your squad is covered: we will tell you who is gone.',
        some: '{done} squads of {total} covered. For {squads} {missing}: there we do not flag who is gone.',
        someOne:
          '1 squad of {total} covered. For {squads} {missing}: there we do not flag who is gone.',
        none: 'No squad of yours has rows in these files: we do not flag who is gone.',
        missingOne: 'the file is missing',
        missingMany: 'the files are missing',
        goneEyebrow: 'Who is gone',
        goneBefore:
          'For every squad the files cover we will also tell you who is gone: who was registered with you and is not in the file.',
        goneAll: 'We will tell you who is gone in all {total} squads.',
        goneAllOne: 'We will tell you who is gone in your squad.',
        goneSome:
          'We will tell you who is gone in {done} squads of {total}. For {squads} {missing}: there we flag nobody. All the rows go in anyway.',
        goneSomeOne:
          'We will tell you who is gone in 1 squad of {total}. For {squads} {missing}: there we flag nobody. All the rows go in anyway.',
        goneNone:
          'No squad has rows in the files yet: there we flag nobody. All the rows go in anyway.',
        inFile: 'in the file {file}',
        inFiles: 'in the files {files}',
        alsoCovers: 'Covers {squads} too.',
        notThisSquad: 'Its rows are not this squad’s: it covers {squads}.',
        notThisSquadNone: 'Its rows are no squad’s of yours: they go in without a squad.',
        born: 'born in {years}',
        bornBy: 'born in {year} or earlier',
      },
      fileWarnings: {
        kind_unrecognised:
          'We cannot tell the type of {file}: no row says whether it is amateurs or youth. The rows go in all the same.',
        rows_repeated:
          '{count} rows of {file} are in {other} too: whoever appears twice goes in once.',
        rows_repeated_one: '1 row of {file} is in {other} too: whoever appears twice goes in once.',
      },
      row: {
        line: 'row {line}',
        ofTabulato: 'of the register',
        ofFile: 'of {file}',
        known: 'already in Volée',
        fix: 'Fix',
        fixed: 'Details fixed',
        fixAndAdd: 'Fix and add',
        added: 'Added',
        notFromTabulato: 'It does not depend on the register: it cannot be added from here.',
        edited: 'Edited',
        withoutTeam: 'without a squad',
      },
      warnings: {
        tax_code_missing: 'The codice fiscale is missing.',
        tax_code_invalid: 'The codice fiscale is not valid.',
        tax_code_birth_date_mismatch: 'The codice fiscale does not match the date of birth.',
        tax_code_name_mismatch: 'The codice fiscale does not match the name.',
      },
      reasons: {
        tax_code_missing: 'The codice fiscale is missing: fix it and add them.',
        tax_code_invalid: 'The codice fiscale is not valid: fix it and add them.',
        tax_code_birth_date_mismatch:
          'The codice fiscale does not match the date of birth: fix it and add them.',
        tax_code_name_mismatch: 'The codice fiscale does not match the name: fix it and add them.',
        tax_code_conflicts:
          'Volée already has a person with this name and date of birth, but with another codice fiscale.',
        tax_code_taken: 'The codice fiscale already belongs to another person in Volée.',
        figc_number_taken: 'The FIGC number already belongs to another person in Volée.',
        registered_elsewhere: 'Registered with another club this season.',
        minor_official: 'A minor who is an official of the club cannot be registered as well.',
        missingName: 'The register has no name on this row.',
        missingBirthDate: 'The register has no date of birth on this row.',
        nameTooLong: 'The register has a name that is too long on this row.',
        figcNumberTooLong: 'The register has a matricola that is too long on this row.',
        registered_after_season: 'The registration date is after the end of the season.',
        refused: 'We could not register this row: add it by hand.',
      },
      preview: {
        eyebrow: 'Before registering',
        title: '{count} players go into {club}, season {season}',
        titleOne: '1 player goes into {club}, season {season}',
        fromFiles: 'From {files} files, {count} rows.',
        fromOne: 'From the register, {count} rows.',
        known: '{count} are already in Volée: we register them here and fill in what is missing.',
        knownOne: '1 is already in Volée: we register them here and fill in what is missing.',
        nobodyYet: 'Nobody has gone in yet.',
        entering: 'Going in',
        toCheck: 'To check',
        already: 'Already here',
        out: 'Left out',
        toCheckBody:
          'Without a correct codice fiscale these rows do not go in: fix it, or confirm it if it is the one on the card.',
        outBody: 'The other rows go in all the same; these you fix right after, by hand.',
        alreadyBody: 'Already registered here: we leave them as they are.',
        back: 'Back',
        confirm: 'Register {count}',
        confirming: 'Registering…',
      },
      outcome: {
        eyebrow: 'Register uploaded',
        title: '{count} players went in',
        titleOne: '1 player went in',
        where: 'Into {club}, season {season}.',
        already: '{count} were already here.',
        alreadyOne: '1 was already here.',
        outBody: 'Fix what is wrong and add them: the register does not need uploading again.',
        outBodyNone: 'Nothing to fix here.',
        nextEyebrow: 'And now',
        nextTitle: 'The rosters',
        nextBody:
          '{count} players have no team. We suggest the team from the year of birth: check and confirm.',
        nextBodyOne:
          '1 player has no team. We suggest the team from the year of birth: check and confirm.',
        nextAction: 'Build the rosters',
      },
      gone: {
        title: 'No longer in the register',
        body: 'They were registered with you this season and the registers no longer have them. If they have left, release them: they leave the roster and keep their profile.',
        withoutTeam: 'no team',
        uncoveredTitle: 'Uncovered squads',
        uncoveredBody:
          'The files have no rows for {squads}: there we do not flag who is gone. If they have all left, release them from the Players page.',
        addFile: 'Add a file',
      },
      // The detail of a row of «Going in» or «Already here»: what the file says of it.
      detail: {
        description: 'From row {line} {source}.',
        lastName: 'Surname',
        firstName: 'First name',
        birthDate: 'Date of birth',
        sex: 'Sex',
        taxCode: 'Codice fiscale',
        figcNumber: 'FIGC number',
        registeredOn: 'Registration date',
        registeredToday: 'The day you register',
        kind: 'Type',
        squad: 'Suggested squad',
        none: '—',
        close: 'Close',
        edit: 'Edit',
      },
      fix: {
        title: 'Fix {name}',
        description:
          'From row {line} {source}. Fix the codice fiscale, or confirm it if it is the one on the card: the row goes in when you register.',
        descriptionMissing:
          'From row {line} {source}. Type the codice fiscale on the card: the row goes in when you register.',
        close: 'Close',
        taxCode: 'Codice fiscale',
        taxCodeHelp: 'Sixteen characters, as on the health card.',
        lastName: 'Surname',
        splitHelp: 'Split without a codice fiscale: the surname is the first word.',
        firstName: 'First name',
        sex: 'Sex',
        sexPlaceholder: 'Choose one',
        birthDate: 'Date of birth',
        figcNumber: 'FIGC number',
        figcNumberHelp: 'The one on the card. Left empty, the register’s stays.',
        required: 'Required',
        cancel: 'Cancel',
        submit: 'Fix',
        editTitle: 'Edit {name}',
        editDescription:
          'From row {line} {source}. The change holds for this registration: the file stays as it is.',
        save: 'Save',
      },
      // «Fix and add»: the form of the aggiunta a mano, filled from the row that was left out.
      add: {
        title: 'Fix and add',
        description: 'From row {line} {source}. Fix the datum and add them.',
        close: 'Close',
        cancel: 'Cancel',
        submit: 'Add',
        submitting: 'Registering…',
        registered: '{name} is registered.',
        afterSeason:
          'The registration date in the register is after the end of the season: we register them from today.',
      },
    },
  },
  myCoach: {
    title: 'Your bench',
    description: 'The bench incarichi you hold, squad by squad.',
    appointments: 'YOUR INCARICHI',
    noAppointments: 'No bench incarico currently held.',
    // A squad has no name in the model: a club fields one Allievi side, and the split into
    // A and B is what enrolling in two league groups says. So the id, until a label exists.
    team: 'squad {id}',
    teamKits: 'The squad’s kits',
    noTeam: 'No squad',
  },
  // Unassigned club staff can ask for an assignment.
  noSeat: {
    onClub: 'You are on the staff of {club}, without a seat',
    ask: 'Access is given by the club official: ask them for a seat.',
  },
  myParent: {
    title: 'Your family',
    description: 'Your children: those in your care, and those you follow since they turned 14.',
    minors: 'YOUR CHILDREN',
    addChild: 'Add a child',
    childProfile: 'Their profile',
    taxCodeMissing: 'Codice fiscale not recorded',
    noMinors: 'You haven’t added a child yet.',
    requests: 'YOUR REQUESTS',
    requestPending: 'Waiting for the club',
    requestDetail: '{club} · requested on {date}',
    childGuardian: 'Guardian',
    childGuardianWhy: 'Under 14: you are their guardian.',
    // A tutela that ended at 14 (spec #278): the parent follows the player now.
    childFollowed: 'You follow them',
    childFollowedWhy:
      'Turned 14 on {date}: now they manage the profile themselves. You see it until they remove you.',
    childFollowedOf: 'Now they manage the profile themselves. You see it until they remove you.',
    // «Altro tutore» (#297): a link named by the child, which only a guardian sends.
    anotherGuardian: 'Another guardian',
    anotherGuardianTitle: 'Another guardian for {name}',
    anotherGuardianBody:
      'Send this link to the other parent: with an account of their own they become a guardian of {name} like you. Neither of you can remove the other.',
    anotherGuardianEyebrow: 'Link for the guardian',
    anotherGuardianValidUntil: 'Valid until {deadline}',
    anotherGuardianShare: 'Share',
    anotherGuardianWhatsApp: 'Send on WhatsApp',
    anotherGuardianCopy: 'Copy link',
    anotherGuardianCopied: 'Link copied',
    anotherGuardianMessage: 'Hi! Here is the link to become a guardian of {name} on Volée: {link}',
    anotherGuardianClose: 'Close',
    childAge: '{age} years old',
    childWithUsername:
      'Under 14: you are their guardian. They have no e-mail and sign in with the username «{username}»: if they forget the password, you set it.',
    access: 'Access',
    accessTitle: '{name}’s access',
    accessBody:
      '{name} signs in with no e-mail, with the username and the password you choose. Only you change them.',
    accessUsername: 'Username',
    accessUsernameHint: 'Letters, digits and dots, 4 to 20. It is not public.',
    accessPasswordHint: 'Leave it empty to keep the password as it is.',
    accessSave: 'Save',
    accessSaved: '{name}’s access is updated.',
    accessClose: 'Close',
    accessNothing: 'Change the username or type a new password.',
    // «Usa questo telefono anche per …» (#302): the child's account, added with no password of theirs.
    thisPhone: 'Use this phone for {name} too',
    thisPhoneTitle: 'This phone for {name} too',
    thisPhoneBody:
      'Add {name} to this phone: then you pass from your account to theirs from the menu with your picture, top right, without their password.',
    thisPhoneProtectedTitle: 'The important things ask for your password',
    thisPhoneProtectedBody:
      'Confirming, changing {name}’s privacy, the plan, their access and inviting another guardian: even with the phone open on your account, your password will be needed.',
    thisPhoneAdd: 'Add {name} to this phone',
    thisPhoneAdded: '{name} is on this phone: you pass to them from the menu.',
    thisPhoneSwitch: 'Switch to {name}',
    thisPhoneClose: 'Close',
  },
  // What a guardian decides for a child asks the password again (#301): the dialog any such act opens.
  passwordCheck: {
    title: 'Enter your password again',
    body: 'What you decide for your child asks for your password again. Once you give it, we will not ask for a few minutes.',
    label: 'Your password',
    confirm: 'Confirm',
    confirming: 'Checking…',
    cancel: 'Cancel',
    close: 'Close',
  },
  // The internal twin of /iscrizione (task 10): the guardian is already signed in, so this
  // page skips the two steps that are about their own identity — no e-mail, no password.
  addChild: {
    title: 'Add a child',
    description:
      'Your child’s own registration, with their e-mail and a password of theirs. You are already inside, so you confirm it as you send it.',
    backToFamily: 'your family',
  },
  // Shared by the two schede that draw this form; the field labels live at each surface's
  // own catalogue instead — apps/frontend/docs/quattro-profili-sotto-me.md.
  playerFacts: {
    nationalitySearch: 'Search a country',
    nationalityEmpty: 'No country with this name',
    edit: 'Correct the details',
    save: 'Save',
    cancel: 'Cancel',
    saved: 'Details saved.',
  },
  // The lines that address the reader on `/me/player`. On `/players/$personId` they
  // are about somebody else, so the voice changes and the rest of `myPlayer` stays one.
  otherPlayer: {
    editProfile: 'Edit their profile',
    privacyTitle: 'Who sees their profile',
    privacyClosedLine: 'Outside whoever holds a seat in their club, nobody opens it.',
    privacyRecord: 'their record',
    privacyClub: 'their squad',
    privacyCareer: 'their career',
    privacyPhoto: 'their photo',
    privacyStats: 'the statistics of their matches',
    privacyAlways: 'Whoever holds a seat in their club always sees everything.',
    privacyPreview: 'See how others see them',
    notRegistered: 'Not registered with any club this season.',
    recordTitle: 'Their record',
    careerTitle: 'Their career',
    careerLabel: 'Every club they have been registered with',
  },
  // The reader's-side vocabulary of a profile they do not own, distinct from `otherPlayer`
  // (which only substitutes voice for lines `myPlayer` already says).
  player: {
    profileNotPublic: 'This player does not show their profile.',
  },
  myPlayer: {
    backToPlayers: 'the players',
    toTheRecord: 'See the registration data',
    backToTeam: 'the squad',
    notFound: 'This player does not exist.',
    title: 'Your profile',
    toClub: 'Go to the club {club}',
    notRegistered: 'You are not registered with any società this season.',
    lastMatchTitle: 'Last match',
    nextMatchTitle: 'Next match',
    score: 'Result',
    lastMatchEyebrow: 'Matchday {matchday} · {day}',
    versus: 'vs {club}',
    calledUp: 'Called up · No. {number}',
    calledUpNoNumber: 'Called up',
    replyComing: 'Coming',
    replyNotComing: 'Not coming',
    replyOpen: 'Reply',
    privacyTitle: 'Who sees your profile',
    privacyOpen: 'Open',
    privacyClosed: 'Closed',
    privacyClosedLine: 'Outside whoever holds a seat in your club, nobody opens it.',
    privacyOpenNothing: 'Anyone on Volée opens it, but sees no section.',
    privacyOpenShows: 'Anyone on Volée sees {sections}.',
    privacyRecord: 'your record',
    privacyClub: 'your squad',
    privacyCareer: 'your career',
    privacyPhoto: 'your photo',
    privacyStats: 'the statistics of your matches',
    privacyAlways: 'Whoever holds a seat in your club always sees everything.',
    privacyChange: 'Change',
    privacyPreview: 'See how others see it',
    familyTitle: 'Account and family',
    familyPrompt: 'Another child to enrol?',
    familyAction: 'Enrol a child',
    crestOf: 'The crest of {club}',
    recordTitle: 'Your record',
    age: 'Age',
    ageValue: '{years} years',
    ageMissing: 'Not recorded',
    foot: 'Foot',
    footPlaceholder: 'Not declared',
    nationality: 'Nationality',
    nationalityPlaceholder: 'Not declared',
    figcNumber: 'FIGC number',
    figcNumberMissing: 'Not recorded',
    editRecord: 'Correct',
    careerTitle: 'Your career',
    careerLabel: 'Every società you have been tesserato with',
    careerCurrent: 'current',
    noCareer: 'No tesseramento recorded yet.',
    editProfile: 'Edit your profile',
    previewBanner: 'You are seeing your profile as other people see it.',
    previewExit: 'Leave the preview',
  },
  /** The lines of `myPlayerEdit` that address the reader, said about somebody else: a tutore
   *  edits a child's profile, and «show my profile» would be a sentence about the wrong
   *  person. Substitution over `myPlayerEdit`, the device `otherPlayer` already uses. */
  otherPlayerEdit: {
    title: 'Edit their profile',
    description: 'Their details, and who sees them.',
    contactTitle: 'Their contact details',
    contactDescription: 'The number their club reaches them on.',
    contactReserved:
      'Name, date of birth and codice fiscale are what the club registered them with: the club corrects them.',
    visibilityTitle: 'Who sees their profile',
    visibilityDescription:
      'Their club always sees everything. These decide what anyone else on Volée reads.',
    isPublic: 'Show their profile to other people on Volée',
    showsRecord: 'Their record: age, position, foot, nationality',
    showsClub: 'The società and the squad they play for now',
    showsCareer: 'The seasons they played before',
    closedHint: 'Their profile is closed: nobody outside their società can open it.',
    preview: 'See their profile as others see it',
    backToProfile: 'their profile',
    photoTitle: 'Their photo',
    photoDescription: 'It shows on their profile. Square, and you choose the crop.',
    cropTitle: 'Frame their photo',
    showsPhoto: 'Their photo',
    showsStats: 'The statistics of their matches',
  },
  myPlayerEdit: {
    backToProfile: 'your profile',
    title: 'Edit your profile',
    description: 'Your details, and who sees them.',
    contactTitle: 'Your contact details',
    contactDescription: 'The number your club reaches you on.',
    contactReserved:
      'Name, date of birth and codice fiscale are what your club registered you with: they correct them.',
    visibilityTitle: 'Who sees your profile',
    visibilityDescription:
      'Your club always sees everything. These decide what anyone else on Volée reads.',
    isPublic: 'Show my profile to other people on Volée',
    showsRecord: 'My record: age, position, foot, nationality',
    showsClub: 'The società and the squad I play for now',
    showsCareer: 'The seasons I played before',
    closedHint: 'Your profile is closed: nobody outside your società can open it.',
    saved: 'Saved.',
    save: 'Save',
    preview: 'See your profile as others see it',
    plan: 'Your plan',
    photoTitle: 'Your photo',
    photoDescription: 'It shows on your profile. Square, and you choose the crop.',
    uploadPhoto: 'Choose a photo',
    replacePhoto: 'Replace the photo',
    removePhoto: 'Remove',
    cropTitle: 'Frame your photo',
    cropConfirm: 'Use this',
    cropPhone: 'On a phone',
    cropWide: 'On a computer',
    cropCancel: 'Cancel',
    photoTooLarge: 'The photo must weigh less than {maxMb} MB.',
    photoUnreadable: 'That file could not be opened as an image. Try another one.',
    photoSaved: 'Photo saved.',
    photoRemoved: 'Photo removed.',
    showsPhoto: 'My photo',
    showsStats: 'The statistics of my matches',
    statsHint:
      'The statistics show only together with the squad of the match: this season’s if «the club and the squad» is on too, earlier ones if the seasons before are on too.',
  },
  // «Chi ti segue» on the player's own edit page (spec #278): who held the tutela until 14.
  followers: {
    title: 'Who follows you',
    description:
      'Whoever follows you sees your profile and your highlights. They do not see your favourites and playlists, are not emailed about you and decide nothing.',
    remove: 'Remove',
    removeTitle: 'Remove {name}?',
    removeBody:
      'They will no longer see your profile. For now {name} cannot follow you again: that comes later.',
    removeCancel: 'Cancel',
    removed: '{name} no longer follows you.',
    // What they were: the relationship of the tutela that ended at 14.
    was: {
      mother: 'Mother · was your guardian',
      father: 'Father · was your guardian',
      guardian: 'Legal guardian · was your guardian',
    },
  },
  // «Da oggi decidi tu»: the step after the first access from 14 (spec #278, prototype screen 6).
  autonomy: {
    badge: '14 years',
    title: 'From today, you decide',
    description: 'You have turned 14: your profile is yours to manage.',
    descriptionOne:
      'You have turned 14: your profile is yours to manage. {names} now follows you: sees your profile, decides nothing, and you can remove them.',
    descriptionMany:
      'You have turned 14: your profile is yours to manage. {names} now follow you: they see your profile, decide nothing, and you can remove them.',
    consentTitle: {
      mother: 'The consent your mother gave',
      father: 'The consent your father gave',
      guardian: 'The consent your guardian gave',
    },
    consentBody:
      'When you enrolled, {name} gave it. It is yours now: to withdraw it, write to privacy@volee-football.com.',
    consentTitleUnknown: 'The consent given for you',
    consentBodyUnknown:
      'When you enrolled, whoever held your guardianship gave it. It is yours now: to withdraw it, write to privacy@volee-football.com.',
    enter: 'Enter',
    emailTitle: 'To go on you need an e-mail of your own',
    emailBody:
      'Until now you signed in with «{username}». From today the password is yours, and recovering it needs an e-mail: once confirmed, you sign in with that.',
  },
  players: {
    title: 'Players',
    description: 'Protected area with a multiple selection that survives a route change.',
    selected: 'Players selected',
    clearSelection: 'Clear the selection',
    positionFilter: 'Filter by position',
    allPositions: 'All positions',
  },
  // The squads of the società. A squad has no name in the model: it is category
  // plus Under year plus gender, which is why every label is composed rather than stored.
  teams: {
    title: 'Squads',
    filters: 'Filters',
    genderGroup: 'Gender',
    allGenders: 'All',
    ageGroupGroup: 'Category',
    allAgeGroups: 'All',
    empty: 'This società has no squad yet.',
    // A società with no squad at all: the ink card that leads to writing to Volée.
    emptyCard: {
      title: 'No squad',
      body: 'Volée enters the squads from the LND groups of the season. If one is missing, even among the Pulcini and the Esordienti, write to Volée.',
      write: 'Write to Volée',
    },
    noMatch: 'No squad matches these filters.',
    clearFilters: 'Clear',
    resultCount: '{count} squads',
    resultCountOne: '{count} squad',
    // The question on top of the page until the società says the squads are its own.
    confirmation: {
      eyebrow: 'First steps · Squads',
      title: 'Are these your squads?',
      // {origin}: from the LND groups of the current season, with its name when the calendar is there.
      detail:
        'Volée entered the {count} squads below {origin}. If one is missing, wrong or not yours, write to Volée.',
      detailOne:
        'Volée entered the squad below {origin}. If it is wrong, not yours or another one is missing, write to Volée.',
      origin: 'from the LND groups of the season',
      originSeason: 'from the {season} LND groups',
      confirm: 'These are ours',
      confirmed: 'Squads confirmed.',
      write: 'Write to Volée',
    },
    // The panel that opens an email already written to Volée: Volée enters the squads, the società reports.
    writeToVolee: {
      title: 'Write to Volée',
      description: 'Volée enters the squads. If one is missing, wrong or not yours, tell us.',
      close: 'Close',
      teamsLegend: 'Which squads are not right?',
      noteLabel: 'What is missing or has to change?',
      notePlaceholder: 'E.g. an Esordienti squad is missing.',
      messageLabel: 'The message',
      address:
        'An email already written opens to {email}. Without a mail app, write to that address.',
      cancel: 'Cancel',
      send: 'Write to Volée',
      // The email: subject and message, which the panel shows as it is and the button opens.
      subject: 'Squads to fix · {club}',
      greeting: 'Hello Volée, this is {club}.',
      teamsOne: 'Among our squads is {teams}, which is not right. Can you fix it or remove it?',
      teamsMany:
        'Among our squads are {teams}, which are not right. Can you fix them or remove them?',
    },
  },
  // R19/R20 · the convocazione list shared by /me/coach and /me/official.
  teamSheetList: {
    upcomingTitle: 'UPCOMING CALL-UPS',
    upcomingEmpty: 'No match on the calendar.',
    // On the next match's card, when at least one convocato said «not coming».
    notComing: '{count} not coming',
    // Beside the title, the rail's card one stands on: «2 / 5».
    upcomingPosition: '{current} / {total}',
    backlogTitle: 'OVERDUE CALL-UPS',
    backlogReason: 'Without a call-up, the players get no highlights.',
    versus: 'VS',
    versusSpoken: 'versus',
    actionNone: 'Prepare the call-up',
    actionDraft: 'Resume the draft',
    actionPublished: 'See the call-up',
    statusDraft: 'Draft',
    // The backlog by squad: what the figure counts, the card's note and one match's row.
    backlogCount: 'to publish',
    backlogOldest: 'the oldest is from {when}',
    backlogMore: 'Show the other {n} squads',
    backlogMoreOne: 'Show the other squad',
    backlogLess: 'Show less',
    backlogVersus: 'vs',
    atHome: 'at home',
    away: 'away',
  },
  // One squad.
  team: {
    // The head of the page: the club's own band, read as the squad's.
    hero: {
      mister: 'Coach {names}',
      // Past two coaches, the first two and how many are the others.
      misterMore: 'Coach {names} +{more}',
      girone: 'Group {code}',
      gironePlace: 'Group {code} · place {position}',
      points: 'Points',
      // The crest is the link to the club: its name says where it leads, not what it draws.
      toClub: 'Go to the club {club}',
    },
    cover: {
      title: 'THE SQUAD’S COVER',
      note: 'A landscape photo of the whole squad, with the players in the middle: the squad’s page cuts it itself, for a phone and for a computer, as you see here.',
      phone: 'Phone',
      wide: 'Computer',
      upload: 'Choose a photo',
      replace: 'Replace',
      remove: 'Remove',
      saved: 'Cover saved.',
      removed: 'Cover removed.',
      tooLarge: 'Even shrunk, the photo is over {maxMb} MB: choose another one.',
      unreadable: 'That file could not be opened as an image: choose another one.',
    },
    backToTeams: 'the squads',
    backToTeam: 'the squad',
    // Only from B on: the federation writes the first side as the bare club name.
    squadLetter: 'Squad {letter}',
    headCoachFromAppointment: 'from the season’s incarico',
    change: 'change',
    seatsTitle: 'TEAM SEATS',
    seatFree: 'Free seat',
    seatsFull: 'The team seats are all taken: remove someone to free one up.',
    revokeOfferOf: 'Revoke the invitation to {email}',
    seatRole: 'On the seat, as',
    giveToClubStaff: 'Give it to someone on the club’s staff',
    noClubStaff: 'Nobody else on the club’s staff can take this seat.',
    pickPerson: 'Choose',
    inviteOntoSeat: 'Invite someone onto this seat',
    letIn: 'Invite to sign in',
    letInOf: 'Invite {name} to sign in',
    removeCoach: 'Remove',
    removeCoachOf: 'Remove {name}',
    removeCoachTitle: 'Remove {name} from the squad {squad}?',
    removeCoachBody:
      'They leave the seat and the squad: no more rosa, enrolment link or call-ups. The seat stays free, and they stay on the club’s staff.',
    removeStaffBody: 'They leave the squad’s staff. They stay on the club’s staff.',
    seatGiven: 'Seat given.',
    staffAdded: 'Added to the squad’s staff.',
    seatFreed: 'Removed from the squad: the seat is free.',
    staffRemoved: 'Removed from the squad’s staff.',
    bothYears: '1st and 2nd year',
    cancel: 'Cancel',
    enrolmentsEmpty: 'This squad is not entered in any group this season.',
    rosterTitle: 'THE ROSTER',
    rosterCount: '{count} {noun}',
    calendarEmpty: 'No fixture on the calendar yet.',
    notFound: 'This squad does not exist.',
    inviteLink: {
      title: 'Enrolment link',
      explain:
        'Whoever opens this link enrols their child into this leva. Send it to the team’s WhatsApp group.',
      durationLabel: 'How long it lasts',
      durations: { 24: '24 hours', 72: '3 days', 168: '7 days' },
      activate: 'Activate the link',
      activating: 'Activating…',
      reactivate: 'Reactivate',
      stateActive: 'Active',
      stateExpired: 'Expired',
      expiresAt: 'Expires on {date} at {time}',
      // Not «Expired on…»: the badge beside this line already says «Expired».
      expiredOn: 'on {date} at {time}',
      loading: 'Loading the link…',
      linkLabel: 'Enrolment link',
      copyLabel: 'Copy',
      copiedMessage: 'Link copied',
      revealLabel: 'Show the link',
      hideLabel: 'Hide the link',
      keepPrivate: 'Share it only with the families of this leva.',
      rotate: 'Rotate the link',
      rotateTitle: 'Rotate the enrolment link of the squad {squad}?',
      rotateBody:
        'The current link stops working at once: whoever has it will need the new one, which starts now with the same duration. Enrolment requests already received stay where they are.',
      rotateYes: 'Rotate',
      deactivate: 'Switch off',
      deactivateTitle: 'Switch off the enrolment link of the squad {squad}?',
      deactivateBody:
        'Whoever has the link will not be able to use it any more, and the squad stays without a link until you activate a new one. Enrolment requests already received stay where they are.',
      deactivateYes: 'Switch off',
      // The message the sender's WhatsApp opens with. «your child» always: one sentence for every leva.
      share: {
        label: 'Send on WhatsApp',
        copyLabel: 'Copy the message',
        copiedMessage: 'Message copied',
        withClub: 'Here is the link to enrol your child in {team} at {club} on Volée: {link}',
        withoutClub: 'Here is the link to enrol your child in {team} on Volée: {link}',
        validUntil: 'The link is valid until {date} at {time}.',
      },
    },
  },
  // The squad's page in tabs: Matches, Roster, Staff (spec #390).
  teamPage: {
    tabsLabel: 'The squad’s sections',
    tabMatches: 'Matches',
    tabRoster: 'Roster',
    tabStaff: 'Staff',
    nextTitle: 'THE NEXT ONE',
    resultsTitle: 'THE LATEST RESULTS',
    resultsEmpty: 'No result yet.',
    nextEmpty: 'No match scheduled.',
    allMatches: 'All matches',
    allMatchesShown: 'Only the latest',
    allMatchesTitle: 'ALL THE MATCHES',
    championshipTitle: 'THE CHAMPIONSHIP',
    outcomeWon: 'W',
    outcomeDrawn: 'D',
    outcomeLost: 'L',
    outcomeWonSpoken: 'won',
    outcomeDrawnSpoken: 'drawn',
    outcomeLostSpoken: 'lost',
    // The Roster section of whoever manages the squad: the search, the rows or table, the acts.
    roster: {
      searchLabel: 'Search for a player',
      filterLabel: 'Filter the roster',
      filterAll: 'All',
      listLabel: 'The roster',
      noAccount: 'No account',
      hasAccount: 'Active',
      age: '{age} years old',
      columnPlayer: 'Player',
      columnRole: 'Position',
      columnAge: 'Age',
      columnAccount: 'Account',
      columnTaxCode: 'Tax code',
      columnActs: 'Actions',
      add: 'Add',
      addTitle: 'Add to the roster',
      addNote:
        'The tesserati of the società without a squad. Whoever is not yet a tesserato signs up with the enrolment link.',
      addClose: 'Close',
      menuOf: 'Actions for {name}',
      roleAct: 'Position on the pitch',
      roleTitle: 'Position on the pitch: {name}',
      roleClose: 'Close',
      removeAct: 'Take out of the roster',
    },
    // The Staff section (spec #390, #396).
    staff: {
      title: 'The squad’s staff',
      listLabel: 'The squad’s staff',
      empty: 'Nobody on the staff.',
      clubAccessTitle: 'Access through the club',
      accountMissing: 'No account',
      accountPending: 'Invitation pending',
      managedByClub: 'The società’s dirigenti give and take the staff: you only see it.',
      assign: 'Assign',
      assignTitle: 'Who takes the seat?',
      assignAfterRemovalTitle: 'The seat is free: put someone on it?',
      assignDescription:
        'Choose someone from the club’s staff. Whoever has no account takes it all the same, and you invite them afterwards.',
      leaveFree: 'Leave it free',
      panelClose: 'Close',
    },
    // The actions in the head of whoever manages the squad (spec #390, #396).
    hero: {
      coverAction: 'Change the cover',
      coverDone: 'Done',
      panelClose: 'Close',
    },
  },
  // A società's first and second kit, shared by all its squads (CONTEXT.md § «Le divise»).
  kits: {
    title: 'The club’s kits',
    description: 'The first and the second kit, shared by all your squads.',
    backToClub: 'the club',
    notAuthorised: 'Not authorised',
    notAuthorisedBody: 'The kits of this club are changed by whoever runs it.',
    notFound: 'This club does not exist.',
    teamsTitle: 'The squads’ kits',
    teamsEmpty: 'No squad has kits of its own: they wear the club’s.',
    teamsOpen: 'Open their kits',
    design: 'design',
    primaryColor: 'main colour',
    secondaryColor: 'second colour',
    shortsColor: 'shorts',
    pickPrimary: 'Pick the main colour',
    pickSecondary: 'Pick the second colour',
    pickShorts: 'Pick the shorts colour',
    notChosen: 'not chosen yet',
    save: 'Save',
    remove: 'Remove',
    saved: '{kit} saved.',
    removed: '{kit} removed.',
    required: 'Required field.',
    // The colours taken from a photo of the kit: packages/ui/docs/divise-e-contagocce.md § «Il contagocce legge un'area, e la lente dice quale».
    photo: {
      choose: 'Choose or take a photo',
      change: 'Change photo',
      note: 'The photo stays on your phone: it is read here, not uploaded or saved.',
      opening: 'Opening the photo…',
      unreadable: 'This photo does not open here: try a JPEG or a PNG.',
      photoLabel: 'The kit’s photo: tap a colour on it',
      stepsLabel: 'The colour you are taking',
      position: '{position} of {total}',
      hint: 'A tap reads a small area, not a point: a fold or a shadow does not count.',
      doneTitle: 'Done: the colours are taken',
      doneHint: 'To correct one, press its colour below and tap the photo again.',
      redo: 'redo',
      shirt: 'Shirt',
      second: 'Second colour',
      shorts: 'Shorts',
      tapShirt: 'Tap the shirt',
      tapSecond: 'Tap the second colour of the shirt',
      tapShorts: 'Tap the shorts',
      redoShirt: 'Redo: tap the shirt',
      redoSecond: 'Redo: tap the second colour of the shirt',
      redoShorts: 'Redo: tap the shorts',
    },
  },
  // A squad's own first and second kit, besides the club’s (CONTEXT.md § «Le divise»). The form,
  // the colours and the saved/removed sentences are `kits`’s: this is only what names the squad.
  teamKits: {
    title: 'The squad’s kits',
    description: 'The squad’s first and second kit: they come in addition to the club’s.',
    notAuthorisedBody:
      'The kits of this squad are changed by the club and whoever holds a seat on the squad.',
  },
  // The roster of one squad.
  roster: {
    allPositions: 'all',
    noPosition: 'position not recorded',
    overAge: 'Over age',
    roleOf: 'Position of {name}',
    roleNote: 'The position holds for the player’s profile, not only for this squad.',
    roleChanged: '{name} now plays as {role}',
    added: '{name} is in the roster',
    addedMany: '{count} players are in the roster',
    removeTitle: 'Take {name} out of the rosa of {squad}?',
    removeBody:
      'They can no longer be called up with this squad. They stay a tesserato of the società, without a squad.',
    removeYes: 'Take out',
    removed: '{name} is out of the roster',
    // «Without a squad»: the active tesserati who are in no roster (CONTEXT.md).
    unassignedSearchLabel: 'search among those without a squad',
    unassignedPositionGroup: 'Filter those without a squad by position',
    unassignedListLabel: 'The tesserati without a squad',
    unassignedEmpty: 'No tesserato is without a squad.',
    addUnassigned: 'Add',
    addUnassignedCount: 'Add {count}',
    addUnassignedCancel: 'Cancel',
    // Who matches and is already elsewhere: saying so, or they would read as not tesserati.
    alreadyElsewhere: '{name} is already in {team}',
    emptyTitle: 'No player in this roster',
    // The empty rosa of whoever composes: the way to fill it, and the families' own way in.
    emptyBody:
      'The tesserati enter the roster from «Build the rosters», after the register. The families can also enrol their kids on their own, with the squad’s link.',
    emptyCompose: 'Build the rosters',
    emptyInvite: 'Invite the players and the families',
    noMatch: 'No player matches these filters.',
  },
  // The match. One page for everyone: the public version is this one filtered —
  // whoever watches from outside sees fewer data, not another screen.
  match: {
    competition: '{competition} · group {group}',
    whenAndWhere: '{date} · {time} · {pitch}',
    timeAndPitch: 'at {time} · {pitch}',
    statusFinal: 'Full time',
    goalTimeline: 'The goals of the match',
    notPlayedYet: 'not played yet',
    sideCategory: '{ageGroup} U{underAge}',
    sideCategoryBothYears: '{ageGroup}, 1st and 2nd year',
    watch: 'Watch the match',
    recordedMatch: 'the recorded match · {minutes} min',
    recordingTitle: 'The match',
    recordingLength: '{minutes} minutes · full recording',
    unlock: 'Unlock the match',
    highlightMinute: "{minute}'",
    lockedClip: 'Unlock to watch',
    tabHighlights: 'highlights',
    tabScoreLines: 'match report',
    tabMeasures: 'statistics',
    highlightsTitle: 'THE HIGHLIGHTS',
    highlightCount: '{count}',
    highlightSummary: '{minute}’ · {action} · {player}',
    seeAllHighlights: 'See all the highlights',
    noHighlights: 'No highlight for this match.',
    scoreLinesTitle: 'THE MATCH REPORT',
    scoreLineSummary: '{minute}’ · {action} · {player}',
    noScoreLines: 'The match report is empty.',
    seeLineup: 'See the call-ups',
    onPitchTitle: 'THE PLAYERS ON THE PITCH',
    seeAllPlayers: 'See all the players',
    squadRosterTitle: '{club} · THE CALL-UP',
    editLineup: 'EDIT',
    // The call-up as the families read it: the answer for each of their own, then the squad.
    callUp: {
      calledTitle: '{name} is called up',
      number: 'No. {number}',
      meetingAtPitch: 'Meet at {time} at the pitch',
      meetingAtPlace: 'Meet at {time}, {place}',
      notCalledTitle: '{name} is not in the squad list for this match',
      notCalledCoach: 'For any question, ask the coach, {coach}.',
      yourChild: 'Your child',
      you: 'You',
      shirt: 'Shirt number {number}',
      status: 'Called up',
      notCalledEyebrow: 'The call-up',
      meetingLabel: 'Meeting',
      meetingPitch: 'At the pitch',
      rosterLabel: 'Called up',
      answerLabel: 'Answer for {name}',
      answerComing: 'I am coming',
      answerNotComing: 'I am not coming',
      notComingTitle: '{count} not coming',
      answersLine: '{coming} coming · {notComing} not coming · {open} no answer',
      markComing: 'Coming',
      markNotComing: 'Not coming',
      markOpen: 'No answer',
      answeredBy: 'by {name}',
      // The state of the convocazione, in the pill of its card on the match page.
      summaryToCall: 'To call up',
      summaryDraft: 'Draft',
      summaryPublished: 'Published',
      summaryBacklog: 'Overdue',
      summaryPlayed: 'Goes to the analysis',
      summarySent: 'Sent to the analysis',
      calledCount: 'called up',
      calledCountOne: 'called up',
      hintToCall: 'Choose who comes, the kit and the meeting: then publish it to the families.',
      hintBacklog:
        'Choose who was called up, with their numbers: without a call-up, the players get no highlights.',
      hintDraft: 'Draft saved {when}. The families do not see it.',
      hintDraftUnsaved: 'The families do not see it until you publish it.',
      savedWhen: '{day} at {time}',
      callUpSquad: 'Call up the squad',
      resumeDraft: 'Resume the draft',
      notesLabel: 'Notes',
      answersLabel: 'Answers',
      // Where the convocati will be, while there is none to show.
      rosterEmpty: 'The players called up will appear here.',
      draftChosen: '{count} players chosen, not published yet.',
      draftChosenOne: '{count} player chosen, not published yet.',
      // The band the flow lands on, after «Publish» or «Update».
      landedPublishedText:
        '{count} called up: the families already see it below. Only the message to the group is left.',
      landedPublishedTextOne:
        '{count} called up: the families already see it below. Only the message to the group is left.',
      landedUpdatedText:
        'The families already see the changes below. Tell the group again, if needed.',
    },
    inChampionship: 'THE MATCH IN THE CHAMPIONSHIP',
    matchday: 'matchday {matchday}',
    seeStandings: 'See the group’s standings',
    processingTitle: 'Recording being processed',
    processingBody:
      'The match is over and the system is still working on the video. Highlights and measures appear when it is done.',
    unavailableTitle: 'Recording not available',
    unavailableBody:
      'No video for this match: the camera was faulty or absent, or the match was played elsewhere.',
    trackingFailedTitle: 'Players not recognised',
    trackingFailedBody:
      'The video is there but the system did not recognise the players, so there are no individual measures and no automatic highlights. The result and the match report stand.',
    notFound: 'This match does not exist.',
    changeReports: {
      open: 'Report a change',
      title: 'Report a change to the match',
      description:
        'Tell us even before the change is official: Volée checks it with the federation and fixes the match.',
      close: 'Close',
      cancel: 'Cancel',
      submit: 'Send the report',
      sending: 'Sending…',
      sent: 'Report sent: Volée will check it.',
      date: 'Date',
      time: 'Time',
      postponed: 'Postponed, date to be decided',
      pitch: 'Ground',
      pitchSame: 'The same',
      pitchListed: 'Another on Volée',
      pitchWritten: 'Another ground',
      pitchSearch: 'Search the ground by name',
      pitchSearching: 'Searching…',
      noPitchMatches: 'No ground with this name: choose «Another ground» and write it.',
      pitchWrittenLabel: 'Name and town of the ground',
      note: 'Note',
      noteHint: 'Where the change comes from: a comunicato, a call from the federation, the club.',
      sectionTitle: 'REPORTED CHANGES',
      reportedBy: '{name}, {when}',
      someone: 'Someone',
      saysKickOff: 'New kick-off: {when}',
      saysPitch: 'Ground: {pitch}',
      saysPostponed: 'Postponed, date to be decided',
      staffReplyBy: 'Volée replies:',
      status: { open: 'Waiting', resolved: 'Resolved', dismissed: 'Closed' },
    },
  },
  // One highlight, and the sharing the consent has not settled.
  highlight: {
    backToMatch: 'the match',
    clip: 'the cut action · {seconds} s',
    context: '{minute}’ · {side} · {origin}',
    inThisAction: 'WHO IS IN THIS ACTION',
    playerNote: 'The player of the action opens their profile — area 50 · Player profile.',
    shareTitle: 'SHARE THIS HIGHLIGHT',
    copyLink: 'copy the link',
    download: 'download',
    share: 'share',
    shareBlocked:
      'How much can be shared depends on the consent to video publication and on the visibility level: both are open decisions. Whether and how a minor’s highlight is shared outside the platform is not decided.',
    minorBlocked:
      'Who may watch and share a minor’s highlight depends on the consent to video publication, which in the model is per season and per società. To be decided with the privacy consultant.',
    otherHighlights: 'THE OTHER HIGHLIGHTS OF THE MATCH',
    noOtherHighlights: 'No other highlight for this match.',
    unavailableTitle: 'The video cannot be watched',
    unavailableBody:
      'Either the consent to video publication was withdrawn, or the file is not there.',
    whatItWas: 'WHAT ACTION IT WAS',
    unavailableBlocked:
      'Does the fact that the action exists stay visible even without the video? And if the cause is a withdrawn consent, what is said to the boy: telling a fault from a withdrawal is a choice that touches privacy, not only the message.',
    notFound: 'This highlight does not exist.',
  },
  // The group standings. Each group has its own: they never add up.
  standings: {
    title: 'The group’s standings',
    columnPosition: '#',
    columnClub: 'CLUB',
    columnPlayed: 'P',
    columnWon: 'W',
    columnDrawn: 'D',
    columnLost: 'L',
    columnGoalsFor: 'GF',
    columnGoalsAgainst: 'GA',
    columnGoalDifference: 'GD',
    columnPoints: 'PTS',
    note:
      'Ordered by points, goal difference, goals scored and the club name. It is not the ' +
      'FIGC classifica avulsa and it does not indicate promotion or relegation.',
  },
  // The championships a società plays. A girone is the unit: entries, calendar
  // and standings all belong to one, and never add up across two.
  matches: {
    title: 'Matches',
    empty: 'No fixture in this calendar yet.',
    emptyFiltered: 'No fixture matches these filters.',
    scopeAll: 'All',
    scopeUpcoming: 'Upcoming',
    scopePlayed: 'Played',
    filters: 'Filters',
    clearFilters: 'Clear',
    team: 'Squad',
    anyTeam: 'Every squad',
    group: 'Girone',
    anyGroup: 'Every girone',
    venue: 'Where',
    venueAnywhere: 'Anywhere',
    venueHome: 'Home',
    venueAway: 'Away',
    from: 'From',
    to: 'To',
    withPostponed: 'Show postponed fixtures too',
    resultCount: '{count} fixtures',
    resultCountOne: '{count} fixture',
    postponed: 'Postponed',
    postponedFilter: 'With postponed',
    periodFrom: 'From {date}',
    periodTo: 'Until {date}',
    periodBetween: 'From {from} to {to}',
    versus: 'VS',
    versusSpoken: 'versus',
    seeMatch: 'Open the match',
    scoreSpoken: '{home} to {away}',
    // The filter that keeps the fixtures with no published call-up; its chip says the same words.
    toPrepareFilter: 'To call up',
  },
  leagues: {
    title: 'Your championships',
    listTitle: 'THE GROUPS OF THE SEASON',
    groupCode: 'group {code}',
    withdrawn: 'withdrawn',
    ourClub: 'your club',
    thisSquad: 'this squad',
    empty: 'No squad of the società is entered in a group this season.',
    seasonTitle: 'The championships',
    seasonEmpty: 'No group has been opened for this season yet.',
    enrolledCount: '{count} entered',
    backToLeagues: 'the championships',
    notFound: 'This group does not exist.',
    entriesSection: 'entered',
    calendarSection: 'calendar',
    standingsSection: 'standings',
    entriesTitle: 'THE SQUADS ENTERED',
    entriesEmpty: 'No squad is entered in this group yet.',
    calendarTitle: 'THE CALENDAR',
    calendarEmpty: 'No fixture on this group’s calendar yet.',
    standingsEmpty: 'No side of this group has a standings row yet.',
    standingsTitle: 'THE STANDINGS',
    notPlayedYet: 'No matchday played yet: the order is the alphabetical one.',
  },
  // The call-up, in three steps: who is coming, the kit, the summary.
  lineup: {
    backToMatch: 'the match',
    empty: 'No tesserato of this club can be called up yet.',
    notFound: 'We cannot find your team for this match.',
    back: 'Back',
    stepsLabel: 'Steps of the call-up',
    stepRoster: 'Squad',
    barLabel: 'Call-up controls',
    next: 'Continue',
    rosterTitle: 'PICK THE PLAYERS',
    resumeLast: 'Resume the last call-up',
    searchLabel: 'Search a name',
    calledUpCount: '{count} / 20 called up',
    noNumberNote: 'Whoever has no number will have no statistics or highlights.',
    squadSectionTitle: 'THE SQUAD',
    otherSquadsTitle: 'FROM OTHER SQUADS',
    calledUpLimitReached: 'There are already 20 called up: remove one to add this one.',
    // The field beside a convocato: its name is read, never written; «No.» is the only word shown.
    shirtNumberField: 'Shirt number of {name}',
    shirtNumberPlaceholder: 'No.',
    shirtNumberTaken: 'Number already used',
    // On the bar, above «Continue» and «Publish»: why the way on is shut.
    sharedNumberNote: 'Two called-up players have the same shirt number',
    meetingTimeMissingNote: 'The meeting time is missing',
    // The board of the called up: beside the squad from a wide screen, and in the summary.
    boardTitle: 'Called up',
    boardEmptyRoster: 'Tap a player on the left to call them up: here you write their number.',
    boardEmptySummary: 'Nobody is called up: go back to the squad.',
    boardRemove: 'Let {name} go',
    // The sheet that calls up a tesserato of another squad of the club.
    otherSquads: {
      open: 'Call up from another squad',
      title: 'Call up from another squad',
      search: 'Search by name',
      filterLabel: 'Squad',
      all: 'All',
      noSquad: 'No squad',
      empty: 'Nobody in the other squads by this name.',
      close: 'Close',
      done: 'Done',
    },
    // La divisa di gara: the second step, between the squad and the summary.
    stepKit: 'Kit',
    stepSummary: 'Summary',
    kitTitle: 'THE KIT',
    kitQuestion: 'Which kit does {club} wear? You can also say it later, or after the match.',
    kitChoices: 'The match kit',
    kitProposed: 'Proposed',
    kitOtherDetail: 'Bibs or a third shirt: for this match only',
    kitOtherFromFirst:
      'Filled in with the first kit: change only what differs, the bibs’ shirt for instance.',
    kitOtherFromProposed:
      'Filled in with the proposed kit ({kit}): change only what differs, the bibs’ shirt for instance.',
    kitNoKits:
      'Neither the squad nor the club has its kits yet: for this match you can give other colours.',
    kitSetUpClubKits: 'Set up the club’s kits',
    kitSetUpTeamKits: 'Set up the squad’s kits',
    kitConfirm: 'Confirm: {kit}',
    kitChoose: 'Choose a kit',
    kitLater: 'I will say it later',
    kitKeep: 'Leave it as it is',
    kitRemove: 'Remove the match kit',
    kitNotGiven: 'not given',
    summaryKit: 'Kit',
    summaryCalledUp: '{count} called up',
    summaryTitle: 'READY FOR THE SQUAD',
    // The summary once the match is played: the ritrovo and the notes are read and no longer written.
    summaryTitlePlayed: 'READY FOR THE ANALYSIS',
    playedTitle: 'The match is played: this call-up goes to the analysis.',
    playedNote: 'The meeting and the notes can no longer be written.',
    summaryReview: 'Review the squad',
    edit: 'Edit',
    editWhat: 'Edit {what}',
    changedNotice: 'You have unsaved changes: the families will see them as soon as you save.',
    // Leaving a published call-up with changes the server does not hold.
    leaveTitle: 'Leave without updating?',
    leaveBody: 'The families do not see the changes until you press Update.',
    leaveConfirm: 'Leave',
    leaveStay: 'Stay',
    summaryMissingFigc: '{count} called-up players have no FIGC number.',
    publish: 'Publish the call-up',
    publishedBadge: 'Published',
    draftBadge: 'Draft',
    update: 'Update the call-up',
    confirmedPublished: 'Call-up published',
    confirmedUpdated: 'Call-up updated',
    notifyAgain: 'Notify again',
    numbersTitle: 'SHIRT NUMBERS',
    numbersNote:
      'The match has started: who is called up no longer changes, the numbers still can until the call-up goes to the analysis.',
    numbersSave: 'Save the numbers',
    numbersSaved: 'Numbers saved',
    numbersClosed: 'The call-up has already gone to the analysis: the numbers are final.',
    meetingTitle: 'MEETING POINT AND NOTES',
    meetingTime: 'Meeting time',
    meetingPlace: 'Meeting place',
    meetingPlacePlaceholder: 'At the pitch',
    meetingTimeRequired: 'The time is needed too',
    notes: 'Notes for the families',
    notesCount: '{count} / {max}',
    notify: {
      title: 'Notify the squad',
      published: 'The call-up is published.',
      preview: 'The message',
      whatsapp: 'Send on WhatsApp',
      copy: 'Copy the message',
      copied: 'Message copied',
      close: 'Close',
      // The message the sender's WhatsApp opens with: the names and the kit's name, never its colours.
      message: {
        headline: 'Call-up {team}: {home} – {away}',
        when: '{day}, kick-off {time}, {pitch}',
        kit: 'Kit: {kit}',
        kitFirst: 'first kit',
        kitSecond: 'second kit',
        kitOther: 'other colours, in the link',
        calledUp: '{name} ({number})',
        link: 'Whoever is called up answers here: {link}',
      },
    },
  },
  // What an imported video analysis says, on the match and on a player's profile.
  matchStats: {
    title: 'THE MATCH STATISTICS',
    notYet: 'The statistics arrive when the video analysis of the match is complete.',
    source: 'From the video analysis of the match.',
    teamsLabel: 'The statistics of the two teams',
    playersTitle: '{club} · PLAYER STATISTICS',
    playersLabel: 'The statistics of the players of {club}',
    columnShirt: 'No.',
    columnPlayer: 'Player',
    playersHidden: 'The players’ statistics are seen by whoever holds a seat at their club.',
    profileTitle: 'MATCH STATISTICS',
    profileLabel: 'The statistics match by match',
    columnMatch: 'Match',
    matchCell: '{day} · vs {club}',
    noneYet: 'No analysed match yet.',
    units: { percent: '{value}%', metres: '{value} m' },
    format: 'Match format: {players} a side',
    sevenASide: 'A 7-a-side match: UbiTrack measures fewer statistics.',
    involvementNotComparable:
      'In a 7-a-side match involvement in play is counted on fewer statistics: not comparable with 9 and 11 a side.',
    // The areas in UbiTrack's own grouping (the set document of 15.10.2026), in the order they read.
    areas: {
      shooting: 'Shooting',
      creation: 'Chance creation',
      passing: 'Passing',
      defending: 'Defending',
      setPieces: 'Set pieces',
      discipline: 'Discipline',
      goalkeeping: 'Goalkeeping',
      player: 'Player',
    },
    // The player area as the face-to-face card titles it: a team is not a player.
    teamPlayerArea: 'Other',
    // Keyed by UbiTrack's own names, as `stats` carries them: data/match-stats.ts says which show.
    metrics: {
      shots_total: 'Shots',
      shots_on_target: 'Shots on target',
      shots_off_target: 'Shots off target',
      shots_blocked: 'Shots blocked',
      shot_accuracy_pct: 'Shot accuracy',
      goals: 'Goals',
      assists: 'Assists',
      key_passes: 'Key passes',
      dribbles_successful: 'Successful dribbles',
      dribbles_failed: 'Failed dribbles',
      crosses_attempted: 'Crosses',
      crosses_completed: 'Completed crosses',
      cross_accuracy_pct: 'Cross accuracy',
      passes: 'Passes',
      passes_completed: 'Completed passes',
      pass_accuracy_pct: 'Pass accuracy',
      possession_pct: 'Possession',
      tackles_attempted: 'Tackles',
      tackles_won: 'Tackles won',
      tackle_success_pct: 'Tackle success',
      interceptions: 'Interceptions',
      duels_total: 'Duels',
      duels_won: 'Duels won',
      duels_won_pct: 'Duels won, share',
      aerial_duels_total: 'Aerial duels',
      aerial_duels_won: 'Aerial duels won',
      aerial_duels_won_pct: 'Aerial duels won, share',
      ground_duels_total: 'Ground duels',
      ground_duels_won: 'Ground duels won',
      ground_duels_won_pct: 'Ground duels won, share',
      ball_recoveries: 'Ball recoveries',
      interventions: 'Interventions',
      ball_losses_under_pressure: 'Balls lost under pressure',
      clearances: 'Clearances',
      blocks: 'Blocks',
      corners: 'Corners',
      throw_ins: 'Throw-ins',
      free_kicks: 'Free kicks',
      set_pieces: 'Set pieces',
      fouls_committed: 'Fouls committed',
      fouls_suffered: 'Fouls suffered',
      yellow_cards: 'Yellow cards',
      red_cards: 'Red cards',
      offsides: 'Offsides',
      saves: 'Saves',
      goals_conceded: 'Goals conceded',
      save_pct: 'Save percentage',
      claims_exits_total: 'Claims and exits',
      claims_exits_successful: 'Successful claims and exits',
      claims_exits_success_pct: 'Claims and exits, success',
      goal_kicks_total: 'Goal kicks',
      goal_kicks_successful: 'Successful goal kicks',
      goal_kick_accuracy_pct: 'Goal kick accuracy',
      buildup_involvement_pct: 'Build-up involvement',
      minutes_played: 'Minutes played',
      distance_covered_m: 'Distance covered',
      involvement_pct: 'Involvement in play',
      mistakes: 'Mistakes',
    },
  },
  // A player's highlights on their profile (spec #261): the words are the glossary's, «Gli highlights».
  highlights: {
    title: 'Highlights',
    showcase: 'Featured',
    player: 'The featured highlights',
    previous: 'Previous clip',
    next: 'Next clip',
    play: 'Play',
    pause: 'Pause',
    rail: 'The featured clips',
    latestMatches: 'Latest matches',
    clipEyebrow: "{kind} · {minute}'",
    actions: '{count} actions',
    actionsOne: '{count} action',
    matchSummary: '{actions} · {length}',
    toTheSeason: 'All highlights · {matches}',
    matchCount: '{count} matches',
    matchCountOne: '{count} match',
    backToSeason: 'all highlights',
    backToYourProfile: 'your profile',
    backToTheirProfile: 'their profile',
    matches: 'Matches',
    nothingToShow: 'No highlights to show.',
    clipMinute: "{minute}'",
    matchPlayer: 'The highlights of the match',
    previousAction: 'Previous action',
    nextAction: 'Next action',
    playAll: 'Play all',
    position: '{position} of {total}',
    actionList: 'The actions',
    kindFilter: 'Filter the actions',
    allKinds: 'All',
    playbackFailed: 'We could not play the highlights.',
    // The library: what the player and their guardians choose, keep and collect.
    choose: 'Choose',
    chooseTitle: 'Choose what to feature',
    chooseDescription: 'Up to {places} clips, in the order you tap them.',
    choosable: 'The clips to feature',
    save: 'Save',
    cancel: 'Cancel',
    close: 'Close',
    favourite: 'Favourite',
    more: 'More on this clip',
    feature: 'Feature it',
    unfeature: 'Remove from featured',
    showcaseFull: 'Up to {places} clips can be featured.',
    addToPlaylist: 'Add to a playlist',
    playlists: 'The playlists',
    newPlaylist: 'New playlist',
    createPlaylist: 'Create',
    yourCollections: 'Your collections',
    favourites: 'Favourites',
    clipCount: '{count} clips',
    clipCountOne: '{count} clip',
    collectionSummary: '{clips} · {length}',
    emptyCollection: 'No clips here yet.',
    freeClips: 'Free clips',
    locked: 'Locked',
    upgrade: 'Upgrade your plan',
    inThePlan: 'Included in the plan',
  },
  subscriptions: {
    title: 'Subscriptions',
    description: 'Your plan, if you play, and that of the children you are guardian of.',
    priceLine: '{price}/month per player',
    empty: 'No player to show.',
    uncovered: 'Not subscribed',
    subscribe: 'Subscribe',
    cancel: 'Cancel',
    cancelConfirmTitle: 'Cancel the subscription of {name}?',
    cancelConfirmBody:
      'It stays active until the end of the period already paid, and then it does not renew.',
    cancelConfirm: 'Cancel the subscription',
    cancelKeep: 'Back',
    cancelDone: 'Subscription cancelled.',
    changeCard: 'Change card',
    changeCardTitle: 'Change the payment card',
    changeCardDescription: 'The next renewals will be charged to this card.',
    changeCardSubmit: 'Save the card',
    changeCardPending: 'Saving…',
    changeCardLoading: 'Loading the payment form…',
    changeCardClose: 'Close',
    renewsOn: 'Renews on {date}',
    activeNoDate: 'Active',
    cancelledUntil: 'Cancelled, active until {date}',
    pastDue: 'Payment failed: we are retrying',
    activating: 'Activation in progress…',
    activatingSlow: 'The payment was received: the subscription will appear shortly.',
    // Under 14 the child's own plan is a tutore's to start (spec #278): the link to send them.
    askRow: 'A parent or guardian of yours starts it',
    askTitle: 'Ask your guardian',
    askBody:
      'Under 14, a parent or guardian of yours starts the plan. Send them the link: they open it with their own account and start it for you.',
    askShare: 'Share',
    askWhatsApp: 'Send on WhatsApp',
    askCopy: 'Copy link',
    askCopied: 'Link copied',
    askMessage: 'Hi! Could you start my Volée plan? It is here: {link}',
    notPayer: 'Paid from another account',
    checkoutTitle: 'Subscribe',
    checkoutContinue: 'Continue to payment',
    checkoutPending: 'Paying…',
    checkoutLoading: 'Loading the payment form…',
    checkoutNotFound: 'This player is not among those you can subscribe.',
    paymentsUnavailable: 'Payments are not available in this environment.',
    paymentFailed: "The payment didn't go through. Please try again.",
    backToList: 'Back to subscriptions',
    backToPlan: 'the subscriptions',
  },
  // A control the wireframe draws that no endpoint accepts yet.
  plannedAction: {
    reason: 'Not available yet: no endpoint accepts this change.',
  },
  forbidden: {
    title: 'Access denied',
    description: 'Your profile does not have permission for this section.',
    backToHome: 'Back to the start',
    signOut: 'Sign out',
  },
  notFound: {
    title: 'Page not found',
    description: 'The address you opened does not exist.',
    backToHome: 'Back to the start',
  },
  // The «Cancel» of every window that asks before an act that takes something away (`ConfirmAct`).
  confirm: {
    cancel: 'Cancel',
  },
  loading: {
    pending: 'Loading…',
  },
  pagination: {
    position: 'Page {page} of {pageCount}',
    previous: 'Previous',
    next: 'Next',
  },
  // What every `FilterBar` says the same way: the page keeps only its own nouns.
  filters: {
    remove: 'Remove the filter {filter}',
    active: '{count} active filters',
    activeOne: '{count} active filter',
  },
  error: {
    title: 'Something went wrong',
    retry: 'Try again',
    backToHome: 'Back to the start',
  },
}

/** The shape every translation has to cover, derived from the source above. */
export type UiMessages = typeof EN_UI
